import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react'
import { useSearchParams } from 'react-router'
import { api, ErroreApi, type VocePersona } from '../servizi/api.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { annunciaConversazioneAperta } from '../servizi/avvisi.ts'
import { Attesa, Avviso, Intestazione, Vuoto } from '../componenti/Pezzi.tsx'
import {
  AreaTesto, Bottone, BottoneIcona, Campo, Etichetta, Modale, Riquadro, Scelta, Spunta, Tabella,
} from '../componenti/base.tsx'
import {
  IconaAggancia, IconaAggiorna, IconaAmministrazione, IconaArchivia, IconaAvviso, IconaBusta, IconaCartella, IconaCerca, IconaCestino,
  IconaColonna, IconaDocumento, IconaGraffetta, IconaGruppo, IconaImmagine, IconaIndietro, IconaIngrandisci, IconaInoltra,
  IconaLucchetto, IconaNuovaScheda, IconaOcchio, IconaPiu, IconaRiduci, IconaRipristina, IconaRispondi,
  IconaRispondiATutti, IconaScarica, IconaScrivi, IconaVuoto,
} from '../componenti/Icone.tsx'
import * as posta from '../servizi/posta.ts'
import * as bozze from '../servizi/bozze.ts'
import { BozzeNellaCartella, EtichettaNonInviato, NonInviato } from './posta/BozzeNellaCartella.tsx'
import {
  AvvisoCifratura, AvvisoCifraturaWebmail, RiquadroAvvisoInCalce, sceltaSicurezza,
} from './posta/AvvisoInCalce.tsx'
import { InCancellazione, InConservazione } from './posta/Conservazione.tsx'
import { StatoLettura, unisciStati, useLetturaContinua, useRiletturaElenco } from './posta/LetturaContinua.tsx'
import { Compositore, type AperturaCompositore } from './posta/Compositore.tsx'
import { AssociaCasella, CaselleDiProgetto, FirmaDellaCasella } from './posta/CaselleCondivise.tsx'
import { ArchivioCaselle, ConfermaArchiviazione } from './posta/ArchivioCaselle.tsx'
import { Quarantena } from './posta/Quarantena.tsx'
import { ConfermaApertura, TestoConCollegamenti, useAltezzaCornice, useCollegamentoToccato } from './posta/CollegamentiMessaggio.tsx'
import { Rubrica } from './posta/Rubrica.tsx'
import { ConteggioDaLeggere, SelettoreCaselle } from './posta/SelettoreCaselle.tsx'
import { EtichettaProvenienza } from './posta/EtichettaProvenienza.tsx'
import { Indirizzi } from './posta/Indirizzi.tsx'
import { ComandiMessaggio } from './posta/StampaMessaggio.tsx'
import { ComandoIndesiderata } from './posta/Indesiderata.tsx'
import { AvvisoConferma, ConfermeRicevute } from './posta/ConfermaLettura.tsx'
import { BottoneInoltraComeAllegato } from './posta/InoltraComeAllegato.tsx'
import { percorsoOriginale, preparaAllegatiPerInoltro, preparaInoltroComeAllegato } from '../servizi/posta-messaggio.ts'
import { ConcediInoltro, InoltriInDeroga } from './posta/InoltriInDeroga.tsx'
import { CredenzialiProgramma } from './posta/CredenzialiProgramma.tsx'
import {
  Bandierine, Etichette, FiltroBandierina, FiltroEtichetta, MenuSegni, attributiRiga, type CambioEtichette,
} from './posta/Segni.tsx'
import { AssenzaRegole } from './posta/AssenzaRegole.tsx'
import * as elencoPosta from '../servizi/posta-elenco.ts'
import { BarraSelezione, SelezionaTutte, SpuntaRiga, useSelezione } from './posta/Selezione.tsx'
import {
  AlberoCartelle, GestioneCartelle, SpostaIn, TIPO_TRASCINAMENTO, inOrdineDiAlbero,
} from './posta/CartellePersonali.tsx'
import { PreferenzePosta } from './posta/PreferenzePosta.tsx'
import { AiutoScorciatoie, useScorciatoie } from './posta/Scorciatoie.tsx'
import { RigaMessaggio } from './posta/ElencoMessaggi.tsx'
import { DoveTrovato, NotaRicerca, SceltaAmbito, ambitoEffettivo, suTutteLeCartelle } from './posta/RicercaPosta.tsx'

/**
 * Posta.
 *
 * Le linguette: la webmail, le sezioni «In cancellazione» e «In conservazione» con la regola di
 * cancellazione (500, posta/Conservazione.tsx), le due configurazioni delle caselle e, per chi
 * gestisce le utenze, le caselle di gruppo (483). La webmail dipende dalla funzione 'webmail_completa': finche' non e'
 * attiva l'API risponde 409 e la pagina mostra lo stato vuoto, mentre la configurazione resta
 * operativa.
 *
 * Caselle di gruppo: caselle condivise con indirizzo e password propri (qualita@, privacy@). I
 * membri le trovano nella webmail accanto alla propria, con l'accesso pieno; la vista «Tutte le
 * caselle» le riunisce in una sola lista, e la scelta della vista resta salvata per la persona.
 *
 * Caselle di progetto e livelli (504): la casella di un progetto si comporta come una di gruppo, per il
 * referente, i Project Manager del progetto e i membri. Sulle caselle condivise chi ha «legge» apre le
 * conversazioni senza i comandi che cambiano la casella, e la casella resta fuori da «Tutte le caselle».
 * La linguetta «Caselle condivise» (chi gestisce le utenze) tiene le caselle di gruppo, quelle da
 * associare e quelle di progetto; ?casella=<id> apre una casella gia' scelta (dalla pagina del progetto).
 *
 * Posta ospitata (502): la casella non ha server ne' password, la posta arriva nel gestionale dalla
 * VM di posta e parte da li' (da Scaleway TEM le sole email automatiche della casella dell'ente).
 * Nelle configurazioni una casella ospitata mostra solo il nome, l'indirizzo e la ricezione; la
 * linguetta «Caselle e ricezione» (chi gestisce utenti o posta) elenca tutte le caselle con la
 * modalita', converte quelle IMAP e crea le personali ospitate.
 *
 * Archivio delle caselle (507, posta/ArchivioCaselle.tsx): «Archivia» nella linguetta «Caselle e
 * ricezione» dismette la casella e la porta nell'archivio; la linguetta «Archivio delle caselle» (chi
 * gestisce le utenze, Direzione, Referente privacy) la consulta in sola lettura, con il passaggio a
 * Glacier, le estrazioni e i recuperi.
 *
 * Quarantena (546, posta/Quarantena.tsx): gli allegati infetti stanno in un bucket a parte; nei messaggi il
 * nome con «Infetto, in quarantena: non si scarica», senza comando. La linguetta «Quarantena» (Direzione e
 * Referente privacy) li elenca, li recupera in formato sicuro e li elimina definitivamente.
 *
 * Bozze scritte a piu' mani (505): a sinistra, «Bozze in scrittura» elenca le bozze delle caselle da
 * cui si scrive, aperte da chiunque le usi; «Bozze condivise con te» quelle a cui si e' invitati. Il
 * compositore sta in posta/Compositore.tsx.
 *
 * L'elenco come in Roundcube (541): selezione multipla e barra delle azioni (posta/Selezione.tsx), cartelle
 * personali e «Sposta in…» anche trascinando (posta/CartellePersonali.tsx), vista per messaggi
 * (posta/ElencoMessaggi.tsx), «Elenco e lettura» con le preferenze sul server (posta/PreferenzePosta.tsx),
 * scorciatoie da tastiera (posta/Scorciatoie.tsx).
 *
 * Bandierine ed etichette (538, posta/Segni.tsx): la riga con una bandierina resta del suo colore; il menu
 * «Bandierina ed etichette» sta nella conversazione aperta e su ogni riga; due filtri sopra l'elenco, in tutte
 * le cartelle della casella.
 */

type Linguetta = 'posta' | 'rubrica' | 'cancellazione' | 'conservazione' | 'organizzazione' | 'mia' | 'gruppi' | 'caselle' | 'archivio'
  | 'quarantena'

/**
 * Le linguette che si aprono dall'indirizzo (?linguetta=): l'avviso di preavviso porta a «In
 * cancellazione», quello delle caselle da associare (504) a «Caselle condivise» con `da_associare=1`.
 */
const DA_INDIRIZZO: Linguetta[] = ['rubrica', 'cancellazione', 'conservazione', 'gruppi', 'archivio', 'quarantena']

type Casella = {
  id: string; ambito: string; nome_visualizzato: string; indirizzo: string
  smtp_host: string; smtp_porta: number; smtp_sicurezza: string; smtp_utente: string
  smtp_password: string | null
  imap_host: string | null; imap_porta: number | null; imap_sicurezza: string | null
  imap_utente: string | null; imap_password: string | null; imap_cartelle: string[]
  imap_ultima_sincro: string | null
  attiva: boolean; ultima_prova: string | null; ultimo_errore: string | null
  /** Assente su una casella non ancora salvata. */
  modalita?: posta.Modalita
  ultimo_arrivo_il?: string | null
}

/**
 * Una casella non ancora configurata, con i valori predefiniti dell'ente gia' dentro.
 *
 * Senza i predefiniti resta la forma di prima, che vale per la casella dell'organizzazione:
 * quella e' unica e i suoi parametri sono proprio quelli che l'amministratore sta decidendo.
 */
const vuota = (indirizzo = '', nome = '', p?: Predefiniti | null): Casella => ({
  id: '', ambito: '', nome_visualizzato: nome, indirizzo,
  smtp_host: p?.smtp_host ?? '', smtp_porta: p?.smtp_porta ?? 587,
  smtp_sicurezza: p?.smtp_sicurezza ?? 'starttls', smtp_utente: indirizzo,
  smtp_password: null,
  imap_host: p?.imap_host ?? '', imap_porta: p?.imap_porta ?? 993,
  imap_sicurezza: p?.imap_sicurezza ?? 'tls', imap_utente: indirizzo,
  imap_password: null, imap_cartelle: p?.imap_cartelle?.length ? p.imap_cartelle : ['INBOX'],
  imap_ultima_sincro: null,
  attiva: false, ultima_prova: null, ultimo_errore: null,
})

/**
 * I parametri del server di posta dell'ente, uguali per tutti (CHT-28).
 *
 * Servono a precompilare la casella personale: l'operatore trova server, porte e sicurezza
 * gia' scritti e compila la sola password. Non contengono credenziali e li legge chiunque sia
 * autenticato; li scrive chi ha `impostazione.modifica`.
 */
type Predefiniti = {
  smtp_host: string; smtp_porta: number; smtp_sicurezza: string
  imap_host: string; imap_porta: number; imap_sicurezza: string
  imap_cartelle: string[]
}

/** Giri di ricerca senza risultati che la pagina fa da sola, prima di chiedere di continuare. */
const GIRI_RICERCA_AUTOMATICI = 4

/**
 * Casella condivisa, di gruppo o di progetto (504): il messaggio parte con il nome della casella e la
 * firma di chi scrive, e l'elenco dice chi ha aperto per ultimo ogni conversazione.
 */
const condivisa = (c: { ambito: string }) => c.ambito === 'gruppo' || c.ambito === 'progetto'

const testoErrore = (e: unknown) => (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

const SICUREZZA_SMTP = [
  { valore: 'starttls', etichetta: 'STARTTLS (porta 587)' },
  // Porta 465: la RFC 8314 la dichiara preferibile, ma il server dell'ente risponde su 587 con
  // STARTTLS. L'etichetta lo dice, perche' sceglierla senza cambiare la porta e' l'errore di
  // configurazione piu' frequente e si manifesta come un invio che resta appeso.
  { valore: 'tls', etichetta: 'TLS diretto (porta 465 — cambia anche la porta)' },
  // «nessuna» non e' piu' ammessa (498): vedi sceltaSicurezza per le caselle salvate prima.
]

const SICUREZZA_IMAP = [
  { valore: 'tls', etichetta: 'TLS (porta 993)' },
  { valore: 'starttls', etichetta: 'STARTTLS (porta 143)' },
]

export function Posta() {
  const { io, puo } = useSessione()
  const [parametri, setParametri] = useSearchParams()
  const [linguetta, setLinguetta] = useState<Linguetta>(() => {
    const chiesta = parametri.get('linguetta') as Linguetta | null
    if (chiesta === 'gruppi' && !puo('utente.gestisci')) return 'posta'
    if (chiesta === 'archivio' && !puo('utente.gestisci') && !puo('posta.archivio')) return 'posta'
    if (chiesta === 'quarantena' && !puo('posta.quarantena_gestisci')) return 'posta'
    return chiesta && DA_INDIRIZZO.includes(chiesta) ? chiesta : 'posta'
  })
  // L'avviso delle caselle da associare porta qui anche a pagina gia' aperta: la linguetta segue.
  const chiestaOra = parametri.get('linguetta')
  useEffect(() => {
    if (chiestaOra === 'gruppi' && puo('utente.gestisci')) setLinguetta('gruppi')
  }, [chiestaOra, puo])
  // Le sezioni della conservazione (500): chi usa la posta, e chi gestisce la casella dell'ente.
  const vedeSezioni = puo('posta.usa') || puo('impostazione.modifica')

  return (
    <>
      <Intestazione titolo="Posta"
        sottotitolo="Webmail e configurazione delle caselle." />

      <div className="linguette" role="tablist">
        <button type="button" role="tab" aria-selected={linguetta === 'posta'}
                onClick={() => setLinguetta('posta')}>Posta</button>
        {puo('posta.usa') && (
          <button type="button" role="tab" aria-selected={linguetta === 'rubrica'}
                  onClick={() => setLinguetta('rubrica')}>Rubrica</button>
        )}
        {vedeSezioni && (
          <>
            <button type="button" role="tab" aria-selected={linguetta === 'cancellazione'}
                    onClick={() => setLinguetta('cancellazione')}>In cancellazione</button>
            <button type="button" role="tab" aria-selected={linguetta === 'conservazione'}
                    onClick={() => setLinguetta('conservazione')}>In conservazione</button>
          </>
        )}
        {puo('impostazione.modifica') && (
          <button type="button" role="tab" aria-selected={linguetta === 'organizzazione'}
                  onClick={() => setLinguetta('organizzazione')}>Casella dell’ente</button>
        )}
        <button type="button" role="tab" aria-selected={linguetta === 'mia'}
                onClick={() => setLinguetta('mia')}>Casella personale</button>
        {/* Le caselle condivise, di gruppo e di progetto, le gestisce chi gestisce le utenze. */}
        {puo('utente.gestisci') && (
          <button type="button" role="tab" aria-selected={linguetta === 'gruppi'}
                  onClick={() => setLinguetta('gruppi')}>Caselle condivise</button>
        )}
        {/* Tutte le caselle con la modalita': chi gestisce le utenze o la casella dell'ente (502). */}
        {(puo('utente.gestisci') || puo('impostazione.modifica')) && (
          <button type="button" role="tab" aria-selected={linguetta === 'caselle'}
                  onClick={() => setLinguetta('caselle')}>Caselle e ricezione</button>
        )}
        {/* Archivio delle caselle (507): chi gestisce le utenze, Direzione e Referente privacy. */}
        {(puo('utente.gestisci') || puo('posta.archivio')) && (
          <button type="button" role="tab" aria-selected={linguetta === 'archivio'}
                  onClick={() => setLinguetta('archivio')}>Archivio delle caselle</button>
        )}
        {/* Quarantena degli allegati infetti (546): Direzione e Referente privacy. */}
        {puo('posta.quarantena_gestisci') && (
          <button type="button" role="tab" aria-selected={linguetta === 'quarantena'}
                  onClick={() => setLinguetta('quarantena')}>Quarantena</button>
        )}
      </div>

      {linguetta === 'posta'
        ? <Webmail />
        : linguetta === 'rubrica'
          ? <Rubrica scrivi={(casellaId, indirizzo) => {
            // «Scrivi» dalla rubrica: si torna alla posta con il compositore aperto su quel destinatario.
            setLinguetta('posta')
            setParametri((p) => { p.set('casella', casellaId); p.set('scrivi', indirizzo); p.delete('linguetta'); return p })
          }} />
        : linguetta === 'cancellazione'
          ? <InCancellazione />
          : linguetta === 'conservazione'
            ? <InConservazione />
            : linguetta === 'gruppi'
              ? <CaselleDiGruppo soloDaAssociare={parametri.get('da_associare') === '1'} />
              : linguetta === 'caselle'
                ? <CaselleERicezione />
                : linguetta === 'archivio'
                  ? <ArchivioCaselle />
                  : linguetta === 'quarantena'
                    ? <Quarantena />
                    : <Modulo quale={linguetta} email={io?.email ?? ''} nome={`${io?.nome} ${io?.cognome}`} />}
    </>
  )
}

// --- webmail -----------------------------------------------------------------------------------

/** Colonna delle caselle chiusa, su questo browser: una comodita' di chi legge, non un dato da salvare. */
const LATO_CHIUSO = 'cidis-posta-lato-chiuso'

function latoChiusoSalvato(): boolean {
  try { return window.localStorage.getItem(LATO_CHIUSO) === '1' } catch { return false }
}

/**
 * Webmail a tutta altezza (2/11/2026, «la webmail è troppo piccola, tutto troppo stretto»): le colonne
 * arrivano al fondo della finestra e scorrono ciascuna per conto suo, come in Outlook. Sopra ci sono
 * intestazione, linguette, selettore e avvisi, di altezza variabile: l'altezza si misura, e si rimisura
 * quando cambia la finestra o quello che sta sopra. Sotto i 480 px scorre la pagina.
 */
function useAltezzaFinoAlFondo() {
  const pulizia = useRef<(() => void) | null>(null)
  return useCallback((el: HTMLElement | null) => {
    pulizia.current?.()
    pulizia.current = null
    if (!el) return
    const calcola = () => {
      const dallaCima = el.getBoundingClientRect().top + window.scrollY
      el.style.setProperty('--webmail-altezza', `${Math.max(480, Math.floor(window.innerHeight - dallaCima - 16))}px`)
    }
    calcola()
    // Quello che sta sopra, nello stesso contenitore: avvisi del gestionale (secondo fattore, novita'),
    // intestazione, linguette, selettore. Un avviso che compare, cambia altezza o si chiude sposta la posta
    // senza cambiarne le dimensioni: si osservano i vicini, e l'elenco dei vicini quando cambia.
    const genitore = el.parentElement
    const osservatore = new ResizeObserver(calcola)
    const osservaVicini = () => {
      osservatore.disconnect()
      for (const vicino of genitore?.children ?? []) if (vicino !== el) osservatore.observe(vicino)
    }
    osservaVicini()
    const mutazioni = new MutationObserver(() => { osservaVicini(); calcola() })
    if (genitore) mutazioni.observe(genitore, { childList: true })
    window.addEventListener('resize', calcola)
    pulizia.current = () => {
      osservatore.disconnect(); mutazioni.disconnect(); window.removeEventListener('resize', calcola)
    }
  }, [])
}

function Webmail() {
  const { io, puo } = useSessione()
  // «Apri in Posta» dalla pagina del progetto (504): ?casella=<id> sceglie quella casella.
  const [parametri, setParametri] = useSearchParams()
  const casellaChiesta = parametri.get('casella')
  const [stato, setStato] = useState<'carico' | 'pronto' | 'spento' | 'assente'>('carico')
  const [caselle, setCaselle] = useState<posta.CasellaWebmail[]>([])
  const [supervisioni, setSupervisioni] = useState<posta.Supervisione[]>([])
  // «Tutte le caselle» oppure una casella alla volta; nella vista unica casellaId resta nullo.
  const [vista, setVista] = useState<posta.VistaPosta['modo']>('casella')
  const [casellaId, setCasellaId] = useState<string | null>(null)
  const [lettura, setLettura] = useState<string | null>(null)
  const [cartelle, setCartelle] = useState<posta.Cartella[]>([])
  const [cartelleDi, setCartelleDi] = useState<string | null>(null)
  const [cartellaId, setCartellaId] = useState<string | null>(null)
  const [cerca, setCerca] = useState('')
  const [cercaAttiva, setCercaAttiva] = useState('')
  // La tendina sopra l'elenco sceglie fra aperte e archiviate; il cestino e' una voce della colonna
  // (535): «cestinate» nella tendina era lo stesso elenco con un altro nome.
  const [filtroStato, setFiltroStato] = useState<'aperto' | 'archiviato'>('aperto')
  const [nelCestino, setNelCestino] = useState(false)
  // «Solo non lette» (2/11/2026): vale per la cartella, per «Tutte le caselle» e con la ricerca; lo filtra l'API.
  const [soloNonLette, setSoloNonLette] = useState(false)
  // Bandierina ed etichetta (538): filtri in tutte le cartelle della casella, lo stato (aperte, archiviate,
  // cestino) resta. Scegliere una cartella o il cestino li toglie.
  const [filtroBandierina, setFiltroBandierina] = useState<posta.FiltroBandierina | ''>('')
  const [filtroEtichetta, setFiltroEtichetta] = useState('')
  const filtroSegni = !!filtroBandierina || !!filtroEtichetta
  // Le etichette della casella scelta e le proprie, o quelle della vista unica: menu e filtro.
  const [etichette, setEtichette] = useState<posta.EtichettaPosta[]>([])
  // Preferenze della posta (541), lette dal server con le caselle: righe, ordine, vista, lettura, risposta.
  const [preferenze, setPreferenze] = useState<elencoPosta.PreferenzePosta>(elencoPosta.PREFERENZE_PREDEFINITE)
  const vistaMessaggi = preferenze.vista === 'messaggi'
  const ordineDiverso = preferenze.ordine !== 'data' || preferenze.verso !== 'desc'
  const [righeMessaggi, setRigheMessaggi] = useState<elencoPosta.VoceMessaggio[]>([])
  const [ordinateRecenti, setOrdinateRecenti] = useState<number | null>(null)
  // Il messaggio da aprire nella conversazione, dalla vista per messaggi.
  const [messaggioAperto, setMessaggioAperto] = useState<string | null>(null)
  const [inBlocco, setInBlocco] = useState(false)
  const [esitoBlocco, setEsitoBlocco] = useState<string | null>(null)
  const [gestioneCartelle, setGestioneCartelle] = useState(false)
  const [preferenzeAperte, setPreferenzeAperte] = useState(false)
  const [aiutoTastiera, setAiutoTastiera] = useState(false)
  const [cestino, setCestino] = useState<posta.RiepilogoCestino | null>(null)
  // Nel cestino le conversazioni si mostrano tutte, di qualunque cartella.
  const statoElenco: posta.StatoThread = nelCestino ? 'cestinato' : filtroStato
  const cartellaElenco = nelCestino ? null : cartellaId
  const inInviata = !nelCestino && cartelle.some((c) => c.id === cartellaId && c.tipo === 'inviata')
  const [voci, setVoci] = useState<posta.VoceThread[]>([])
  const [cursore, setCursore] = useState<string | null>(null)
  const [caricoElenco, setCaricoElenco] = useState(true)
  const [threadId, setThreadId] = useState<string | null>(null)
  const [aperto, setAperto] = useState<posta.ThreadAperto | null>(null)
  const [caricoThread, setCaricoThread] = useState(false)
  const [pannello, setPannello] = useState<'caselle' | 'elenco' | 'thread'>('elenco')
  const [errore, setErrore] = useState<string | null>(null)
  const [bozza, setBozza] = useState<AperturaCompositore | null>(null)
  const [elencoBozze, setElencoBozze] = useState<bozze.ElencoBozze>({ mie: [], condivise: [] })
  const [agganciamento, setAgganciamento] = useState(false)
  // Le firme di chi scrive (508): si rileggono a ogni chiusura del compositore, perche' telefoni,
  // testi comuni e firme delle caselle possono essere cambiati nel frattempo.
  const [firme, setFirme] = useState<posta.FirmeDellaPersona | null>(null)
  const richiesta = useRef(0)
  const altezza = useAltezzaFinoAlFondo()
  // Colonna di caselle e cartelle chiusa: lo spazio va all'elenco e alla lettura (2/11/2026).
  const [latoChiuso, setLatoChiuso] = useState(latoChiusoSalvato)
  // Lettura a tutta pagina: senza menu, linguette, caselle ed elenco; anche in una scheda nuova.
  const [intero, setIntero] = useState(false)

  function alternaLato() {
    setLatoChiuso((chiuso) => {
      try { window.localStorage.setItem(LATO_CHIUSO, chiuso ? '0' : '1') } catch { /* resta per questa pagina */ }
      return !chiuso
    })
  }

  // Chiusa la conversazione (archiviata, cestinata, altra cartella) si torna alla posta normale.
  useEffect(() => { if (!threadId) setIntero(false) }, [threadId])
  // Gli avvisi sullo schermo non mostrano il riquadro della conversazione gia' aperta (servizi/avvisi.ts).
  useEffect(() => {
    annunciaConversazioneAperta(threadId)
    return () => annunciaConversazioneAperta(null)
  }, [threadId])

  /** La conversazione aperta in una scheda nuova del browser, a tutta pagina («seconda pagina», 2/11/2026). */
  function apriInNuovaScheda() {
    if (!aperto) return
    const qui = new URLSearchParams({ casella: aperto.thread.casella_id, thread: aperto.thread.id, intero: '1' })
    window.open(`/posta?${qui.toString()}`, '_blank', 'noopener')
  }

  /**
   * Le bozze in corso (505): all'apertura, alla chiusura del compositore e ogni trenta secondi mentre la
   * pagina e' aperta, come l'elenco delle conversazioni. La rilettura periodica e' del temporizzatore e
   * non rinnova la sessione.
   */
  const caricaBozze = useCallback((automatica = false) => {
    bozze.elencaBozze(automatica).then(setElencoBozze).catch(() => undefined)
  }, [])
  useEffect(() => {
    if (stato !== 'pronto' && stato !== 'assente') return
    caricaBozze()
    const t = setInterval(() => caricaBozze(true), 30_000)
    return () => clearInterval(t)
  }, [stato, caricaBozze])

  // Dalla notifica dell'invito si arriva con ?bozza=<id>: la bozza si apre subito.
  useEffect(() => {
    const id = parametri.get('bozza')
    if (!id || (stato !== 'pronto' && stato !== 'assente')) return
    setBozza({ bozza: id })
    setParametri((p) => { p.delete('bozza'); return p }, { replace: true })
  }, [parametri, setParametri, stato])

  /**
   * ?thread=<id> apre quella conversazione (scheda nuova, avvisi delle email arrivate); con ?intero=1 a tutta
   * pagina. Si aspetta che la casella chiesta abbia le sue cartelle: il cambio di casella chiude la
   * conversazione aperta, e la chiuderebbe subito dopo averla aperta.
   */
  useEffect(() => {
    const id = parametri.get('thread')
    if (!id || stato !== 'pronto') return
    if (casellaChiesta && (casellaId !== casellaChiesta || cartelleDi !== casellaId)) return
    setThreadId(id)
    setPannello('thread')
    if (parametri.get('intero') === '1') setIntero(true)
    setParametri((p) => { p.delete('thread'); p.delete('intero'); return p }, { replace: true })
  }, [parametri, setParametri, stato, casellaChiesta, casellaId, cartelleDi])

  /**
   * ?cartella=<id> apre quella cartella della casella (gli avvisi «posta nuova» quando una regola ha spostato il
   * messaggio, 543). Come per ?thread=, si aspettano le cartelle della casella chiesta: caricandole la pagina
   * sceglie la Posta in arrivo, e la scelta va fatta dopo. Una cartella che non c'e' (eliminata) lascia quella.
   */
  useEffect(() => {
    const id = parametri.get('cartella')
    if (!id || stato !== 'pronto' || !casellaId || cartelleDi !== casellaId) return
    if (casellaChiesta && casellaId !== casellaChiesta) return
    // Come scegliCartella, ma senza chiudere la conversazione che ?thread= ha appena aperto.
    if (cartelle.some((c) => c.id === id)) {
      setCartellaId(id); setNelCestino(false); setFiltroBandierina(''); setFiltroEtichetta('')
    }
    setParametri((p) => { p.delete('cartella'); return p }, { replace: true })
  }, [parametri, setParametri, stato, casellaChiesta, casellaId, cartelleDi, cartelle])

  function apriBozza(id: string) {
    setBozza({ bozza: id })
  }

  function chiusaBozza() {
    setBozza(null)
    caricaBozze()
    caricaFirme()
  }

  const caricaFirme = useCallback(() => {
    posta.elencaFirme().then(setFirme).catch(() => undefined)
  }, [])
  useEffect(() => { if (stato === 'pronto') caricaFirme() }, [stato, caricaFirme])

  /** Il 409 a funzione non attiva non e' un errore da mostrare: determina lo stato della pagina. */
  const gestisci = useCallback((e: unknown) => {
    if (posta.interruttoreSpento(e)) { setStato('spento'); return }
    setErrore(testoErrore(e))
  }, [])

  useEffect(() => {
    let vivo = true
    // La vista salvata si legge insieme alle caselle: senza, la pagina mostrerebbe prima la casella
    // personale e poi cambierebbe sotto gli occhi. Se la preferenza non si legge vale la propria.
    // Le preferenze dell'elenco (541) insieme: senza, la prima pagina arriverebbe nell'ordine di sempre.
    Promise.all([posta.elencaCaselle(), posta.leggiVista().catch(() => null),
      elencoPosta.leggiPreferenze().catch(() => null)]).then(([elenco, preferita, preferenzeLette]) => {
      if (!vivo) return
      if (preferenzeLette) setPreferenze(preferenzeLette)
      setCaselle(elenco)
      if (!elenco.length) { setStato('assente'); return }
      setStato('pronto')
      // La casella chiesta dall'indirizzo vale piu' della vista salvata, e non la cambia.
      const chiesta = elenco.find((c) => c.id === casellaChiesta)
      if (chiesta) { setVista('casella'); setCasellaId(chiesta.id); return }
      // All'ingresso una casella sola, mai la vista unica (decisione di Davide del 2/11/2026: le caselle
      // non vanno mescolate): l'ultima scelta, o la personale. «Tutte le caselle» resta nel selettore.
      setCasellaId((elenco.find((c) => c.id === preferita?.casella_id)
        ?? elenco.find((c) => c.mia && c.ambito === 'utente') ?? elenco[0]!).id)
    }).catch((e) => {
      if (!vivo) return
      // Fuori perimetro l'API risponde 404: non c'e' nulla da mostrare.
      if (e instanceof ErroreApi && e.stato === 404) { setStato('assente'); return }
      gestisci(e)
      setStato((s) => (s === 'carico' ? 'assente' : s))
    })
    posta.elencaSupervisioni().then((s) => { if (vivo) setSupervisioni(s) }).catch(() => undefined)
    return () => { vivo = false }
  }, [gestisci, casellaChiesta])

  const casella = useMemo(
    () => caselle.find((c) => c.id === casellaId) ?? null, [caselle, casellaId])
  /**
   * Le caselle da cui si scrive e che entrano in «Tutte le caselle»: la propria e quelle di gruppo.
   * La casella dell'ente e quelle in supervisione restano fuori.
   */
  const caselleMie = useMemo(() => caselle.filter((c) => c.mia), [caselle])
  /** Le caselle del campo «Da» (3/11/2026): fra quelle da cui si scrive, quelle che spediscono. */
  const mittenti = useMemo(() => caselleMie.filter((c) => c.spedisce !== false), [caselleMie])
  const indirizzoDi = useMemo(
    () => new Map(caselle.map((c) => [c.id, c.indirizzo])), [caselle])
  const supervisioniAttive = useMemo(
    () => supervisioni.filter((s) => s.attiva), [supervisioni])
  const cartellaScelta = cartelle.find((c) => c.id === cartellaId) ?? null
  // Le bozze del gestionale della casella scelta, e la sua cartella «Bozze», dove compaiono (3/11/2026).
  const cartellaBozze = cartelle.find((c) => c.tipo === 'bozze') ?? null
  const bozzeDellaCasella = useMemo(
    () => (casellaId ? elencoBozze.mie.filter((b) => b.casella_id === casellaId) : []), [elencoBozze.mie, casellaId])
  const bozzeQui = vista === 'casella' && !nelCestino && !filtroSegni && !cercaAttiva && !soloNonLette
    && filtroStato === 'aperto' && cartellaScelta?.tipo === 'bozze' ? bozzeDellaCasella : []
  // Si scrive dalla casella scelta (archivio, cestino, letto, spostamenti), come vuole l'API (esigiScrittura).
  // La casella propria spenta (3/11/2026) si consulta soltanto.
  const scriveQui = vista === 'casella' && !!casella && casella.livello !== 'legge' && !casella.supervisione
    && !casella.spenta && !io?.impersonazione
  // Cartelle e spostamenti (541): solo sulle caselle ospitate, solo nella vista di una casella.
  const puoSpostare = scriveQui && !!casella?.ospitata && cartelle.some((c) => c.destinazione)
  // Dove si cerca (3/11/2026): la scelta accanto al campo, fra le preferenze. Nel cestino si cerca solo li', nella
  // vista unica in tutte le caselle: li' la scelta non c'e'. «Tutte le caselle» se ce n'e' piu' d'una, non in
  // supervisione.
  const conCaselle = !casella?.supervisione && new Set([...caselleMie.map((c) => c.id), casellaId]).size > 1
  const sceltaRicerca = vista === 'casella' && !nelCestino
  const ambitoAttivo = cercaAttiva && sceltaRicerca ? ambitoEffettivo(preferenze.ricerca, conCaselle) : undefined
  // I risultati di piu' caselle dicono quale, come nella vista unica.
  const fraCaselle = ambitoAttivo === 'caselle'
  // In tutte le cartelle si cerca fra aperte e archiviate: la tendina non conta finche' si cerca.
  const statoIndifferente = !!cercaAttiva && !nelCestino && suTutteLeCartelle(ambitoAttivo, vista === 'tutte')
  function scegliAmbito(ricerca: posta.AmbitoRicerca) {
    setPreferenze((p) => ({ ...p, ricerca }))
    elencoPosta.salvaPreferenze({ ricerca }).catch(() => undefined)
  }

  useEffect(() => {
    setLettura(null)
    if (!casellaId) { setCartelle([]); setCartelleDi(null); setCartellaId(null); return }
    let vivo = true
    setCartelle([]); setCartelleDi(null); setCartellaId(null); setNelCestino(false)
    setThreadId(null); setAperto(null)
    posta.elencaCartelle(casellaId).then((elenco) => {
      if (!vivo) return
      setCartelle(elenco)
      setCartelleDi(casellaId)
      const prima = elenco.find((c) => c.tipo === 'in_arrivo') ?? elenco[0]
      setCartellaId(prima ? prima.id : null)
    }).catch((e) => {
      if (!vivo) return
      gestisci(e)
      // L'elenco delle conversazioni non dipende dalle cartelle: si carica lo stesso, senza filtro.
      setCartelleDi(casellaId)
    })
    return () => { vivo = false }
  }, [casellaId, gestisci])

  // Ricerca differita di mezzo secondo: ogni battuta comporta la decifratura di una pagina.
  useEffect(() => {
    const t = setTimeout(() => setCercaAttiva(cerca.trim()), 500)
    return () => clearTimeout(t)
  }, [cerca])

  /**
   * Una pagina dell'elenco. Con una ricerca (528) l'API esamina un tratto di archivio per volta: se
   * torna senza risultati ma con un cursore, si continua da soli per qualche giro (`giro`), poi si
   * lascia a chi cerca il comando «Cerca nei messaggi più vecchi».
   */
  const caricaPagina = useCallback(async (prima: string | null, giro = 0): Promise<void> => {
    if (vista === 'casella' && (!casellaId || cartelleDi !== casellaId)) return
    const mia = ++richiesta.current
    setCaricoElenco(true)
    try {
      // La ricerca vale per l'elenco che si guarda: fuori dal cestino non lo comprende, come Outlook; nel
      // cestino cerca solo li' (535).
      const filtri = {
        cerca: cercaAttiva || undefined, ambito: ambitoAttivo, stato: statoElenco, non_letti: soloNonLette,
        limite: preferenze.righe,
        bandierina: filtroBandierina || null, etichetta: filtroEtichetta || null, prima_di: prima,
        ordine: preferenze.ordine, verso: preferenze.verso,
      }
      // Vista per singoli messaggi (541): le stesse scelte, una riga per messaggio.
      if (vistaMessaggi) {
        const pm = await elencoPosta.elencaMessaggi(vista === 'tutte' ? null : casellaId!,
          { ...filtri, cartella: vista === 'tutte' ? null : cartellaElenco })
        if (mia !== richiesta.current) return
        setRigheMessaggi((precedenti) => (prima ? [...precedenti, ...pm.messaggi] : pm.messaggi))
        setVoci([])
        setCursore(pm.prossimo_cursore)
        setOrdinateRecenti(pm.ordinate_recenti ?? null)
        setErrore(null)
        if (cercaAttiva && !pm.messaggi.length && pm.prossimo_cursore && giro < GIRI_RICERCA_AUTOMATICI) {
          return caricaPagina(pm.prossimo_cursore, giro + 1)
        }
        return
      }
      // Nella vista unica non ci sono cartelle: appartengono a una casella sola.
      const pagina = vista === 'tutte'
        ? await posta.elencaThreadTutte(filtri)
        : await posta.elencaThread(casellaId!, { ...filtri, cartella: cartellaElenco })
      if (mia !== richiesta.current) return
      setVoci((precedenti) => (prima ? [...precedenti, ...pagina.thread] : pagina.thread))
      setRigheMessaggi([])
      setCursore(pagina.prossimo_cursore)
      setOrdinateRecenti(pagina.ordinate_recenti ?? null)
      setErrore(null)
      if (cercaAttiva && !pagina.thread.length && pagina.prossimo_cursore && giro < GIRI_RICERCA_AUTOMATICI) {
        return caricaPagina(pagina.prossimo_cursore, giro + 1)
      }
    } catch (e) {
      if (mia === richiesta.current) gestisci(e)
    } finally {
      if (mia === richiesta.current) setCaricoElenco(false)
    }
  }, [vista, casellaId, cartelleDi, cartellaElenco, cercaAttiva, statoElenco, soloNonLette, filtroBandierina,
    filtroEtichetta, gestisci, preferenze.righe, preferenze.ordine, preferenze.verso, vistaMessaggi, ambitoAttivo])

  useEffect(() => {
    setVoci([]); setRigheMessaggi([]); setCursore(null); setCaricoElenco(true)
    void caricaPagina(null)
  }, [caricaPagina])

  // Selezione multipla (541): per conversazione, o per messaggio nella vista per messaggi.
  const chiaviRighe = useMemo(() => (vistaMessaggi ? righeMessaggi.map((m) => m.id) : voci.map((v) => v.id)),
    [vistaMessaggi, righeMessaggi, voci])
  const selezione = useSelezione(chiaviRighe)
  /** Le conversazioni delle righe scelte: nella vista per messaggi, quelle dei messaggi. */
  const threadScelti = useMemo(() => (vistaMessaggi
    ? [...new Set(righeMessaggi.filter((m) => selezione.scelte.has(m.id)).map((m) => m.thread_id))]
    : voci.filter((v) => selezione.scelte.has(v.id)).map((v) => v.id)), [vistaMessaggi, righeMessaggi, voci, selezione.scelte])

  const aggiornaVoce = useCallback((id: string, modifiche: Partial<posta.VoceThread>) => {
    setVoci((elenco) => elenco.map((v) => (v.id === id ? { ...v, ...modifiche } : v)))
  }, [])

  /** Le etichette della vista (538): della casella scelta e le proprie, o di tutte le caselle della vista unica. */
  const caricaEtichette = useCallback(() => {
    if (stato !== 'pronto' || (vista === 'casella' && !casellaId)) return
    posta.elencaEtichette(vista === 'tutte' ? null : casellaId).then(setEtichette).catch(() => undefined)
  }, [stato, vista, casellaId])
  useEffect(() => { setEtichette([]); caricaEtichette() }, [caricaEtichette])
  // Un'etichetta che nella vista nuova non c'e' (per tutti di un'altra casella, eliminata) non filtra piu'.
  useEffect(() => {
    if (filtroEtichetta && !etichette.some((e) => e.id === filtroEtichetta)) setFiltroEtichetta('')
  }, [etichette, filtroEtichetta])

  /** Bandierine ed etichette cambiate dal menu: la riga dell'elenco e la conversazione aperta. */
  const segniCambiati = useCallback((threadId: string, segni: posta.SegniThread) => {
    aggiornaVoce(threadId, segni)
    setRigheMessaggi((righe) => righe.map((m) => (m.thread_id === threadId ? { ...m, ...segni } : m)))
    setAperto((t) => (t && t.thread.id === threadId ? { ...t, thread: { ...t.thread, ...segni } } : t))
  }, [aggiornaVoce])

  /** Letta o da leggere: la riga, i messaggi della vista per messaggi, la conversazione aperta. */
  const segnatoLetto = useCallback((threadId: string, letto: boolean) => {
    aggiornaVoce(threadId, { non_letti: letto ? 0 : 1 })
    setRigheMessaggi((righe) => righe.map((m) => (m.thread_id === threadId ? { ...m, letto } : m)))
    const adesso = new Date().toISOString()
    setAperto((t) => (t && t.thread.id === threadId
      ? { ...t, messaggi: t.messaggi.map((m) => ({ ...m, letto_il: letto ? m.letto_il ?? adesso : null })) } : t))
  }, [aggiornaVoce])

  /** Un'etichetta creata, rinominata o eliminata: si rilegge l'elenco e si aggiornano le conversazioni mostrate. */
  const etichetteCambiate = useCallback((cambio: CambioEtichette) => {
    const ritocca = (lista: posta.EtichettaSuThread[]) => (cambio.tipo === 'eliminata'
      ? lista.filter((e) => e.id !== cambio.id)
      : cambio.tipo === 'rinominata' ? lista.map((e) => (e.id === cambio.id ? { ...e, nome: cambio.nome } : e)) : lista)
    if (cambio.tipo === 'creata') setEtichette((prima) => [...prima, cambio.etichetta])
    else {
      setVoci((elenco) => elenco.map((v) => ({ ...v, etichette: ritocca(v.etichette) })))
      setRigheMessaggi((righe) => righe.map((m) => ({ ...m, etichette: ritocca(m.etichette) })))
      setAperto((t) => (t ? { ...t, thread: { ...t.thread, etichette: ritocca(t.thread.etichette) } } : t))
    }
    caricaEtichette()
  }, [caricaEtichette])

  // I messaggi da leggere di ogni casella, per il selettore in cima (2/11/2026): ogni minuto mentre la
  // pagina e' aperta, come richiesta automatica che non rinnova la sessione, e subito dopo letto o non letto.
  const aggiornaConteggi = useCallback(() => {
    posta.elencaCaselle(true).then((elenco) => {
      const per = new Map(elenco.map((c) => [c.id, c.non_letti]))
      setCaselle((prima) => prima.map((c) => (per.has(c.id) ? { ...c, non_letti: per.get(c.id)! } : c)))
    }).catch(() => undefined)
  }, [])
  /** Il conteggio della voce «Cestino»: della casella scelta, o di tutte nella vista unica. */
  const caricaCestino = useCallback((automatica = false) => {
    if (stato !== 'pronto' || (vista === 'casella' && !casellaId)) return
    posta.riepilogoCestino(vista === 'tutte' ? null : casellaId, automatica).then(setCestino).catch(() => undefined)
  }, [stato, vista, casellaId])
  useEffect(() => { setCestino(null); caricaCestino() }, [caricaCestino])

  useEffect(() => {
    if (stato !== 'pronto') return
    const t = setInterval(() => { aggiornaConteggi(); caricaCestino(true) }, 60_000)
    return () => clearInterval(t)
  }, [stato, aggiornaConteggi, caricaCestino])

  // «Scrivi» dalla rubrica: ?scrivi=<indirizzo> apre il compositore su quel destinatario, dalla casella scelta.
  const scriviA = parametri.get('scrivi')
  useEffect(() => {
    if (!scriviA || stato !== 'pronto') return
    const da = mittenti.find((c) => c.id === casellaChiesta) ?? mittenti.find((c) => c.ambito === 'utente') ?? mittenti[0]
    if (da) {
      setBozza({ nuova: {
        casella_id: da.id, a: scriviA, cc: '', ccn: '', oggetto: '',
        testo: posta.testoConFirma(posta.firmaPer(firme, da.id)),
      } })
    }
    setParametri((p) => { p.delete('scrivi'); return p }, { replace: true })
  }, [scriviA, stato, mittenti, casellaChiesta, firme, setParametri])

  useEffect(() => {
    if (!threadId) { setAperto(null); return }
    let vivo = true
    let attesa: ReturnType<typeof setTimeout> | null = null
    setAperto(null); setCaricoThread(true)
    posta.apriThread(threadId).then(async (t) => {
      if (!vivo) return
      setAperto(t)
      // La casella e' quella del thread, non quella scelta: nella vista unica non ce n'e' una.
      const sua = caselle.find((c) => c.id === t.thread.casella_id)
      // Sulla casella condivisa l'API ha gia' scritto l'apertura: l'elenco la mostra subito.
      // Impersonando (524) l'apertura non e' dell'utente: l'API non la segna, l'elenco nemmeno.
      if (sua && condivisa(sua) && io && !io.impersonazione) {
        aggiornaVoce(threadId, {
          aperto_da: { id: io.id, nome: io.nome, cognome: io.cognome },
          aperto_il: new Date().toISOString(),
        })
      }
      // La lettura di una casella sorvegliata non tocca lo stato della casella: il titolare
      // non deve trovare letti i messaggi che non ha aperto lui. Su quella di gruppo lo stato e'
      // condiviso: letto da uno, letto per tutti.
      // Impersonando (524) si guarda e basta: la conversazione resta da leggere com'era, per l'utente.
      // Quando diventa letta lo dice la preferenza (541): all'apertura, dopo qualche secondo, mai da sola.
      const daSegnare = sua?.mia && !io?.impersonazione && t.messaggi.some((m) => !m.letto_il)
        && preferenze.lettura !== 'mai'
      const segna = async () => {
        const segnato = await posta.segnaLetto(threadId, true).then(() => true, () => false)
        if (vivo && segnato) { segnatoLetto(threadId, true); aggiornaConteggi() }
      }
      if (daSegnare && preferenze.lettura === 'dopo') attesa = setTimeout(() => void segna(), preferenze.secondi * 1000)
      else if (daSegnare) await segna()
    }).catch((e) => { if (vivo) gestisci(e) })
      .finally(() => { if (vivo) setCaricoThread(false) })
    return () => { vivo = false; if (attesa) clearTimeout(attesa) }
    // voci e caselle servono solo a sapere se la conversazione era da leggere: non devono riaprirla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, aggiornaVoce, gestisci])

  /**
   * La conversazione aperta, riletta senza chiuderla: dopo un invio, un «Riprova» o un invio non riuscito
   * (3/11/2026) lo stato dei messaggi cambia. Una risposta arrivata dopo che si e' aperta un'altra conversazione
   * non la sostituisce.
   */
  const rileggiThread = useCallback(() => {
    if (!threadId) return
    posta.apriThread(threadId)
      .then((t) => setAperto((prima) => (prima && prima.thread.id === t.thread.id ? t : prima)))
      .catch(() => undefined)
  }, [threadId])

  /** «Riprova» su un messaggio non partito: la stessa riga, lo stesso Message-ID (3/11/2026). */
  async function riprovaInvio(m: posta.Messaggio): Promise<string | null | 'inviato'> {
    const r = await posta.riprovaInvio(m.id)
    ricarica()
    rileggiThread()
    caricaBozze()
    return r.stato_invio === 'inviato' ? 'inviato' : r.errore
  }

  /** «Modifica»: il compositore sulla bozza che ricorda il messaggio, o su una nuova con il suo contenuto. */
  async function modificaInvio(m: posta.Messaggio): Promise<void> {
    const { bozza_id: id } = await posta.modificaInvio(m.id)
    setBozza({ bozza: id })
  }

  /** La casella della conversazione aperta: risposta, firma e comandi seguono quella. */
  const casellaAperta = useMemo(
    () => (aperto ? caselle.find((c) => c.id === aperto.thread.casella_id) ?? casella : null),
    [aperto, caselle, casella])

  // Lettura continua (31/10/2026): mentre la pagina e' aperta le caselle guardate si rileggono ogni
  // minuto e l'elenco ogni trenta secondi (posta/LetturaContinua.tsx). A webmail spenta si leggono
  // le proprie, per l'importazione selettiva nelle schede.
  const guardate = useMemo((): string[] | 'mie' | null => {
    if (stato === 'spento') return 'mie'
    if (stato !== 'pronto') return null
    if (vista === 'tutte') return caselleMie.filter((c) => c.lettura).map((c) => c.id)
    return casella?.lettura ? [casella.id] : null
  }, [stato, vista, caselleMie, casella])
  // Con un altro ordine o per messaggi l'elenco non si rilegge da solo (541): si aggiorna con «Aggiorna».
  const rileggiElenco = useRiletturaElenco({
    filtro: stato === 'pronto' && !cercaAttiva && !vistaMessaggi && !ordineDiverso
      && (vista === 'tutte' || (!!casellaId && cartelleDi === casellaId))
      ? { stato: statoElenco, limite: preferenze.righe, casellaId: vista === 'tutte' ? null : casellaId,
          cartellaId: cartellaElenco, nonLetti: soloNonLette, bandierina: filtroBandierina || null,
          etichetta: filtroEtichetta || null }
      : null,
    richiesta, voci, cursore,
    applica: (nuove, prossimo) => { setVoci(nuove); setCursore(prossimo) },
    cartelle: setCartelle,
  })
  const continua = useLetturaContinua(guardate, rileggiElenco)

  function scegliCasella(id: string) {
    setVista('casella')
    setCasellaId(id)
    setCerca(''); setCercaAttiva('')
    setPannello('elenco')
    // La scelta resta per la prossima volta; se non si salva, la pagina funziona lo stesso.
    posta.salvaVista({ modo: 'casella', casella_id: id }).catch(() => undefined)
  }

  /**
   * Supervisione appena aperta: la casella entra nell'elenco e si apre. La scelta non si salva come
   * vista preferita, perche' la supervisione scade e la casella sparira'.
   */
  async function supervisioneAperta(id: string) {
    const [elenco, attive] = await Promise.all([posta.elencaCaselle(), posta.elencaSupervisioni()])
    setCaselle(elenco)
    setSupervisioni(attive)
    setStato('pronto')
    setVista('casella')
    setCasellaId(id)
    setCerca(''); setCercaAttiva('')
    setPannello('elenco')
  }

  function scegliTutte() {
    setVista('tutte')
    setCasellaId(null)
    setNelCestino(false)
    setCerca(''); setCercaAttiva('')
    setThreadId(null)
    setPannello('elenco')
    posta.salvaVista({ modo: 'tutte', casella_id: null }).catch(() => undefined)
  }

  /** «Aggiorna ora» dalla webmail: per la casella propria e per quelle di gruppo. */
  async function aggiornaCasella() {
    if (!casella) return
    setLettura(null)
    try {
      await posta.aggiornaOra(casella.id)
      setLettura('Lettura richiesta: i messaggi nuovi compaiono entro qualche minuto.')
    } catch (e) { setLettura(testoErrore(e)) }
  }

  /**
   * Una bozza nuova. La casella proposta e' quella scelta, se da li' si spedisce, altrimenti la
   * propria, altrimenti la prima che spedisce (3/11/2026: mai una casella spenta, come quella di Sasha).
   * Il testo parte con la firma di quella casella (508), sotto due righe vuote dove scrivere:
   * la firma della persona, o quella propria della casella condivisa.
   */
  function nuovaBozza() {
    const da = mittenti.find((c) => c.id === casella?.id) ?? mittenti.find((c) => c.ambito === 'utente') ?? mittenti[0]
    if (!da) return
    setBozza({ nuova: {
      casella_id: da.id, a: '', cc: '', ccn: '', oggetto: '',
      testo: posta.testoConFirma(posta.firmaPer(firme, da.id)),
    } })
  }

  /** Scegliere una cartella o il cestino toglie i filtri per bandierina ed etichetta, che valgono su tutte. */
  function togliFiltriSegni() {
    setFiltroBandierina('')
    setFiltroEtichetta('')
  }

  function scegliCartella(id: string) {
    setCartellaId(id)
    togliFiltriSegni()
    setNelCestino(false)
    setThreadId(null)
    setPannello('elenco')
  }

  /** La voce «Cestino» (535): le conversazioni cestinate della casella, o di tutte nella vista unica. */
  function scegliCestino() {
    togliFiltriSegni()
    setNelCestino(true)
    setThreadId(null)
    setPannello('elenco')
    caricaCestino()
  }

  /** Nella vista unica, la voce che torna alle conversazioni dopo il cestino. */
  function esciDalCestino() {
    togliFiltriSegni()
    setNelCestino(false)
    setThreadId(null)
    setPannello('elenco')
  }

  function apri(voce: posta.VoceThread) {
    setMessaggioAperto(null)
    setThreadId(voce.id)
    setPannello('thread')
  }

  /** Dalla vista per messaggi: la conversazione, con quel messaggio aperto. */
  function apriMessaggio(m: elencoPosta.VoceMessaggio) {
    setMessaggioAperto(m.id)
    setThreadId(m.thread_id)
    setPannello('thread')
  }

  function ricarica() {
    if (casellaId) {
      posta.elencaCartelle(casellaId).then(setCartelle).catch(() => undefined)
    }
    caricaCestino()
    void caricaPagina(null)
  }

  /**
   * Un'azione sulle conversazioni scelte (541), o su quelle indicate (trascinamento). Poi si rileggono
   * elenco, cartelle, cestino e conteggi; stato e spostamento chiudono la conversazione aperta se era fra
   * quelle, e svuotano la selezione.
   */
  async function eseguiInBlocco(azione: elencoPosta.AzioneBlocco, thread = threadScelti) {
    if (!thread.length) return
    setInBlocco(true); setEsitoBlocco(null)
    try {
      const esito = await elencoPosta.inBlocco(thread, azione)
      setEsitoBlocco(elencoPosta.testoRifiutate(esito))
      if (azione.tipo === 'stato' || azione.tipo === 'sposta') {
        if (threadId && esito.eseguite.includes(threadId)) { setThreadId(null); setPannello('elenco') }
        selezione.svuota()
      }
      ricarica()
      aggiornaConteggi()
    } catch (e) { gestisci(e) } finally { setInBlocco(false) }
  }

  /** «Sposta in…» della conversazione aperta: esce dall'elenco della cartella, e si torna all'elenco. */
  async function spostaAperta(cartellaId: string) {
    if (!aperto) return
    try {
      await elencoPosta.spostaThread(aperto.thread.id, cartellaId)
      setThreadId(null)
      setPannello('elenco')
      ricarica()
    } catch (e) { gestisci(e) }
  }

  async function segnaTutteLette() {
    if (!cartellaId) return
    try {
      await elencoPosta.segnaCartellaLetta(cartellaId)
      ricarica()
      aggiornaConteggi()
    } catch (e) { gestisci(e) }
  }

  /** Il trascinamento di una riga: le scelte, se la riga e' fra quelle; altrimenti la sua conversazione. */
  function iniziaTrascinamento(e: DragEvent, chiave: string, threadDellaRiga: string) {
    const thread = selezione.scelte.has(chiave) ? threadScelti : [threadDellaRiga]
    e.dataTransfer.setData(TIPO_TRASCINAMENTO, JSON.stringify(thread))
    e.dataTransfer.effectAllowed = 'move'
  }

  async function cambiaStatoThread(nuovo: posta.StatoThread) {
    if (!aperto) return
    const prima = aperto.thread.stato
    try {
      await posta.cambiaStato(aperto.thread.id, nuovo)
      if (nuovo === statoElenco) aggiornaVoce(aperto.thread.id, { stato: nuovo })
      else setVoci((elenco) => elenco.filter((v) => v.id !== aperto.thread.id))
      setThreadId(null)
      setPannello('elenco')
      // Dentro o fuori dal cestino: cambiano il suo conteggio e quelli delle cartelle.
      if ((prima === 'cestinato') !== (nuovo === 'cestinato')) {
        caricaCestino()
        if (casellaId) posta.elencaCartelle(casellaId).then(setCartelle).catch(() => undefined)
        aggiornaConteggi()
      }
    } catch (e) { gestisci(e) }
  }

  /** «È indesiderata» o «Non è indesiderata» (542): la conversazione lascia l'elenco, cambiano le cartelle. */
  function cartellaCambiata() {
    if (!aperto) return
    setVoci((elenco) => elenco.filter((v) => v.id !== aperto.thread.id))
    setThreadId(null)
    setPannello('elenco')
    if (casellaId) posta.elencaCartelle(casellaId).then(setCartelle).catch(() => undefined)
    aggiornaConteggi()
  }

  async function segnaNonLetto() {
    if (!aperto) return
    try {
      await posta.segnaLetto(aperto.thread.id, false)
      segnatoLetto(aperto.thread.id, false)
      aggiornaConteggi()
      setThreadId(null)
      setPannello('elenco')
    } catch (e) { gestisci(e) }
  }

  /** «Segna come letta» (541): con la lettura dopo qualche secondo, o mai da sola. */
  async function segnaLetta() {
    if (!aperto) return
    try {
      await posta.segnaLetto(aperto.thread.id, true)
      segnatoLetto(aperto.thread.id, true)
      aggiornaConteggi()
      if (casellaId) posta.elencaCartelle(casellaId).then(setCartelle).catch(() => undefined)
    } catch (e) { gestisci(e) }
  }

  /**
   * Risposta e inoltro: dell'ultimo messaggio, dai comandi della conversazione, o del messaggio scelto, dai suoi
   * comandi (3/11/2026: gli allegati stanno spesso in un messaggio precedente, e Roundcube inoltra quello scelto).
   */
  function rispondi(tipo: 'rispondi' | 'tutti' | 'inoltra', scelto?: posta.Messaggio) {
    const casella = casellaAperta
    if (!aperto || !aperto.messaggi.length || !casella) return
    const ultimo = scelto ?? aperto.messaggi[aperto.messaggi.length - 1]!
    const oggetto = scelto?.oggetto || aperto.thread.oggetto
    // La firma va sopra la citazione, dove si scrive.
    const firma = posta.firmaPer(firme, casella.id)
    if (tipo === 'inoltra') {
      const inoltro = {
        casella_id: casella.id, a: '', cc: '', ccn: '',
        oggetto: /^i:\s/i.test(oggetto) ? oggetto : `I: ${oggetto}`,
        testo: posta.testoConFirma(firma, posta.citazione(ultimo)),
      }
      if (!ultimo.allegati.length) { setBozza({ nuova: inoltro }); return }
      // Con gli allegati del messaggio, come in Roundcube (539): l'API li copia come allegati del compositore,
      // tranne quelli non ancora controllati dall'antivirus, che il compositore elenca. Si tolgono uno per uno.
      preparaAllegatiPerInoltro(ultimo.id).then((r) => setBozza({ nuova: {
        ...inoltro, allegati: r.allegati,
        ...(r.omessi.length ? {
          nota: `Allegati del messaggio rimasti fuori: ${r.omessi.map((o) => `«${o.nome}» (${o.motivo})`).join(', ')}.`,
        } : {}),
      } }), gestisci)
      return
    }
    // Rispondendo a tutti: gli altri destinatari in copia, senza il proprio indirizzo e senza
    // ripetere chi e' gia' destinatario principale.
    const principali = ultimo.direzione === 'entrata' ? [ultimo.da] : ultimo.a
    const esclusi = new Set([casella.indirizzo, ...principali]
      .map((i) => posta.soloIndirizzo(i).toLowerCase()))
    const copie: string[] = []
    for (const indirizzo of [...ultimo.a, ...ultimo.cc]) {
      const chiave = posta.soloIndirizzo(indirizzo).toLowerCase()
      if (esclusi.has(chiave)) continue
      esclusi.add(chiave)
      copie.push(indirizzo)
    }
    // La risposta parte dalla casella che ha ricevuto la conversazione, senza scelta.
    setBozza({ nuova: {
      casella_id: casella.id,
      a: principali.join(', '),
      cc: tipo === 'tutti' ? copie.join(', ') : '',
      ccn: '',
      oggetto: /^r:\s|^re:\s/i.test(oggetto) ? oggetto : `R: ${oggetto}`,
      // Sopra o sotto la citazione, secondo la preferenza (541).
      testo: elencoPosta.testoRisposta(firma, posta.citazione(ultimo), preferenze.risposta),
      // Si cita il Message-ID, non l'identificativo di riga: e' il primo a determinare la
      // conversazione di destinazione e a uscire nella catena In-Reply-To/References. Puo'
      // mancare sui messaggi importati privi di quell'intestazione: in quel caso la risposta
      // apre una conversazione nuova, ed e' il comportamento corretto.
      in_risposta_a: ultimo.message_id ?? undefined,
    } })
  }

  /**
   * «Inoltra come allegato» (539): l'API ricompone l'ultimo messaggio come .eml e lo carica; il compositore si
   * apre con il file allegato, come «Inoltra». Gli allegati non ancora controllati dall'antivirus restano fuori, e
   * il compositore lo dice.
   */
  async function inoltraComeAllegato(scelto?: posta.Messaggio) {
    const casella = casellaAperta
    if (!aperto || !aperto.messaggi.length || !casella) return
    const ultimo = scelto ?? aperto.messaggi[aperto.messaggi.length - 1]!
    const pronto = await preparaInoltroComeAllegato(ultimo.id)
    const oggetto = scelto?.oggetto || aperto.thread.oggetto
    setBozza({ nuova: {
      casella_id: casella.id, a: '', cc: '', ccn: '',
      oggetto: /^i:\s/i.test(oggetto) ? oggetto : `I: ${oggetto}`,
      testo: posta.testoConFirma(posta.firmaPer(firme, casella.id), ''),
      allegati: [pronto.allegato],
      ...(pronto.omessi.length ? {
        nota: `Il messaggio è allegato come file .eml. Restano fuori ${pronto.omessi.map((o) => `«${o.nome}» (${o.motivo})`).join(', ')}.`,
      } : {}),
    } })
  }

  // Le scorciatoie da tastiera (541): su queste righe, sulla conversazione aperta, sulla selezione.
  const puoCambiareAperta = !!casellaAperta && casellaAperta.livello !== 'legge' && !casellaAperta.supervisione
    && !casellaAperta.spenta
  useScorciatoie(stato === 'pronto', !!io?.impersonazione, {
    cestino: () => {
      if (nelCestino) return
      if (threadScelti.length) void eseguiInBlocco({ tipo: 'stato', stato: 'cestinato' })
      else if (aperto && puoCambiareAperta) void cambiaStatoThread('cestinato')
    },
    rispondi: () => { if (aperto && casellaAperta?.mia) rispondi('rispondi') },
    rispondiATutti: () => { if (aperto && casellaAperta?.mia) rispondi('tutti') },
    inoltra: () => { if (aperto && casellaAperta?.mia) rispondi('inoltra') },
    chiudi: () => {
      if (threadId) {
        const id = threadId
        setThreadId(null); setIntero(false); setPannello('elenco')
        requestAnimationFrame(() => document.querySelector<HTMLElement>(`.posta-voce[data-thread="${id}"]`)?.focus())
      } else selezione.svuota()
    },
    selezionaTutte: () => selezione.tutte(true),
    aiuto: () => setAiutoTastiera(true),
  })

  if (stato === 'carico') return <Attesa testo="Caricamento…" />

  if (stato === 'spento') {
    return (
      <div className="vuoto">
        <IconaLucchetto />
        <p>La webmail non è attiva.</p>
        <p className="vuoto-nota">
          La funzione «Webmail e archiviazione integrale della posta» non è attiva: i
          presupposti dell’attivazione sono indicati nella documentazione. La configurazione
          delle caselle e l’invio restano operativi.
        </p>
        {/* Importazione selettiva accesa: le proprie caselle si rileggono lo stesso. */}
        {continua.modo === 'selettiva' && (
          <p className="posta-lettura-sola">
            <StatoLettura stato={unisciStati(continua.stati)} avvioImmediato={continua.avvioImmediato} />
          </p>
        )}
      </div>
    )
  }

  // Il compositore: messaggio nuovo, risposta, oppure una bozza che c'e' gia' (505).
  const compositore = bozza && (
    <Compositore
      key={'bozza' in bozza ? bozza.bozza : 'nuova'}
      caselle={caselleMie}
      apertura={bozza}
      firme={firme}
      chiudi={chiusaBozza}
      spedito={() => { chiusaBozza(); ricarica(); rileggiThread() }}
      // Non partito (3/11/2026): il compositore resta aperto; l'elenco mostra il messaggio nelle «Bozze».
      nonInviato={() => { ricarica(); rileggiThread() }}
    />
  )

  if (stato === 'assente') {
    return (
      <>
        {errore && <Avviso tipo="errore">{errore}</Avviso>}
        <Vuoto testo="Nessuna casella disponibile. La configurazione è nella linguetta «Casella personale»." />
        {/* Chi e' invitato a scrivere una bozza puo' non avere una casella propria (505). */}
        {elencoBozze.condivise.length > 0 && (
          <Riquadro titolo="Bozze condivise con te">
            <VociBozze voci={elencoBozze.condivise} mostraCasella apri={apriBozza} />
          </Riquadro>
        )}
        {compositore}
        {/* Chi chiede di leggere una casella altrui, e chi concede le supervisioni, puo' non avere
            una casella propria. */}
        {(puo('posta.supervisiona') || puo('posta.supervisione_concedi')) && (
          <Riquadro titolo="Supervisione">
            {puo('posta.supervisione_concedi') && <SupervisioniDaControllare />}
            {puo('posta.supervisiona') && (
              <RichiesteSupervisione aperta={(id) => void supervisioneAperta(id).catch(gestisci)} />
            )}
          </Riquadro>
        )}
        {/* La Referente privacy legge i collegamenti agli allegati anche senza una casella propria (509). */}
        {puo('posta.collegamenti_registro') && <RegistroCollegamenti />}
        {/* E gli inoltri in deroga, con la revoca (536). */}
        {puo('posta.inoltri_controlla') && <InoltriInDeroga />}
      </>
    )
  }

  return (
    <>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {/* Caselle salvate con la sicurezza «nessuna»: invio e lettura sospesi finche' non si corregge. */}
      {(vista === 'tutte' ? caselleMie : casella ? [casella] : []).map((c) => (
        <AvvisoCifraturaWebmail key={c.id} casella={c} />
      ))}

      {/* Selettore stile Outlook (2/11/2026): ogni casella con i suoi messaggi da leggere, anche sul telefono. */}
      <SelettoreCaselle caselle={caselle} caselleMie={caselleMie} vista={vista} casellaId={casellaId}
                        scegliCasella={scegliCasella} scegliTutte={scegliTutte} />

      <div className="webmail" ref={altezza} data-pannello={pannello}
           data-lato={latoChiuso ? 'chiuso' : undefined} data-intero={intero && aperto ? 'si' : undefined}>
        <section className="posta-lato">
          <div className="posta-titoletto">Caselle</div>
          {/* La vista unica ha senso da due caselle in su: con una sola e' la casella stessa. */}
          {caselleMie.length > 1 && (
            <button className={`posta-scelta posta-casella${vista === 'tutte' ? ' attiva' : ''}`}
                    aria-current={vista === 'tutte'} onClick={scegliTutte}>
              <span className="riga">
                Tutte le caselle
                <ConteggioDaLeggere n={caselleMie.reduce((t, c) => t + (c.non_letti ?? 0), 0)} />
              </span>
              <span className="indirizzo">in una sola lista</span>
            </button>
          )}
          {caselle.map((c) => {
            const scelta = vista === 'casella' && c.id === casellaId
            return (
              <button key={c.id}
                      className={`posta-scelta posta-casella${scelta ? ' attiva' : ''}`}
                      aria-current={scelta} onClick={() => scegliCasella(c.id)}>
                <span className="riga">
                  {c.nome_visualizzato || c.indirizzo}
                  {c.ambito === 'gruppo' && <Etichetta tono="accento">gruppo</Etichetta>}
                  {c.ambito === 'progetto' && <Etichetta tono="accento">progetto</Etichetta>}
                  {c.livello === 'legge' && <Etichetta>sola lettura</Etichetta>}
                  {c.spenta && c.ambito === 'utente' && <Etichetta>spenta</Etichetta>}
                  {c.ambito === 'organizzazione' && <Etichetta>ente</Etichetta>}
                  {c.supervisione && <Etichetta tono="critico">supervisione</Etichetta>}
                  <ConteggioDaLeggere n={c.non_letti} />
                </span>
                <span className="indirizzo">{c.indirizzo}</span>
                {c.supervisione && (
                  <span className="indirizzo">
                    fino al {new Date(c.supervisione.scade_il).toLocaleDateString('it-IT')}
                  </span>
                )}
                {(vista === 'tutte' ? c.mia : scelta) && (
                  <StatoLettura stato={continua.stati.get(c.id)} avvioImmediato={continua.avvioImmediato} />
                )}
              </button>
            )
          })}
          {/* Nella vista unica non ci sono cartelle: le conversazioni di tutte le caselle e il loro cestino. */}
          {vista === 'tutte' && (
            <>
              <div className="posta-titoletto">Cartelle</div>
              <button className={`posta-scelta${!nelCestino ? ' attiva' : ''}`} aria-current={!nelCestino}
                      onClick={esciDalCestino}>
                <IconaCartella />
                <span className="posta-cartella-nome">Conversazioni</span>
              </button>
              <VoceCestino attiva={nelCestino} cestino={cestino} scegli={scegliCestino} />
            </>
          )}
          {vista === 'casella' && (
            <>
              <div className="posta-titoletto">Cartelle</div>
              {/* Casella propria spenta (3/11/2026): si consulta soltanto, e da qui non si spedisce. */}
              {casella?.spenta && casella.ambito === 'utente' && (
                <p className="posta-nota posta-nota-spenta">
                  Casella spenta: non spedisce. Si consulta soltanto; per scrivere usa un’altra casella.
                </p>
              )}
              {casella?.mia && casella.spedisce === false && casella.non_spedisce && (
                <p className="posta-nota posta-nota-spenta">{casella.non_spedisce}</p>
              )}
              {cartelle.length === 0 && <p className="posta-nota">Nessuna cartella sincronizzata.</p>}
              {/* Con un filtro per bandierina o etichetta si guarda in tutte le cartelle: nessuna e' scelta. Le
                  righe trascinate sulle cartelle vi si spostano (541). */}
              <AlberoCartelle cartelle={cartelle} sceltaId={!nelCestino && !filtroSegni ? cartellaId : null}
                              scegli={scegliCartella} nome={nomeCartella}
                              conteggio={(c) => (c.tipo === 'bozze' ? bozzeDellaCasella.length + c.messaggi : c.non_letti)}
                              titolo={(c) => (c.tipo === 'cestino' ? TITOLO_CESTINO_SERVER : undefined)}
                              rilascia={puoSpostare ? (id, thread) => void eseguiInBlocco({ tipo: 'sposta', cartella_id: id }, thread) : null} />
              <VoceCestino attiva={nelCestino} cestino={cestino} scegli={scegliCestino} />
              {/* Cartelle personali (541): sulle caselle ospitate, per chi ha l'accesso pieno. */}
              {casella?.mia && casella.ospitata && !io?.impersonazione && (
                <button type="button" className="posta-gestisci-cartelle" onClick={() => setGestioneCartelle(true)}>
                  <IconaCartella /> Cartelle…
                </button>
              )}
              {/* Casella ospitata: niente da leggere, la posta arriva da sola. Prima del passaggio
                  dell'MX la ricezione non ha mai consegnato niente, e la casella non riceve ancora. */}
              {casella?.ospitata && (casella.ricezione_attiva || casella.ultimo_arrivo_il
                ? (
                  <p className="posta-nota">
                    Ricezione: {posta.ultimoMessaggio(casella.ultimo_arrivo_il)}
                  </p>
                )
                : <p className="posta-nota">{posta.RICEZIONE_NON_ATTIVA}</p>)}
              {/* «Aggiorna ora» per la casella scelta, propria o di gruppo: ne chiede la lettura. */}
              {casella?.mia && casella.lettura && (
                <Bottone variante="discreto" dimensione="piccolo" icona={<IconaAggiorna />}
                         onClick={() => void aggiornaCasella()}>
                  Aggiorna ora
                </Bottone>
              )}
              {lettura && <p className="posta-nota">{lettura}</p>}
              {/* Risposta di assenza e regole di smistamento (540): per chi ha l'accesso pieno alla casella. */}
              {casella?.mia && <AssenzaRegole key={casella.id} casella={casella} applicate={ricarica} />}
            </>
          )}

          {/* Bozze in corso (505): nella vista unica, di tutte le caselle; in una casella stanno nella sua
              cartella «Bozze» (3/11/2026), e qui solo se la casella non ne ha una. */}
          {(() => {
            const mie = vista === 'tutte'
              ? elencoBozze.mie
              : cartellaBozze ? [] : bozzeDellaCasella
            return mie.length > 0 && (
              <>
                <div className="posta-titoletto">Bozze in scrittura</div>
                <VociBozze voci={mie} mostraCasella={vista === 'tutte'} apri={apriBozza} />
              </>
            )
          })()}
          {elencoBozze.condivise.length > 0 && (
            <>
              <div className="posta-titoletto">Bozze condivise con te</div>
              <VociBozze voci={elencoBozze.condivise} mostraCasella apri={apriBozza} />
            </>
          )}

          {supervisioniAttive.length > 0 && (
            <>
              <div className="posta-titoletto">Supervisioni in corso</div>
              {supervisioniAttive.map((s) => (
                <VoceSupervisione key={s.id} supervisione={s}
                  // Il titolare non revoca la supervisione sulla propria casella, nemmeno se le
                  // concede (rotte/webmail.ts): qui revoca solo il supervisore che rinuncia.
                  puoRevocare={s.ruolo === 'supervisore'}
                  revocata={() => {
                    setSupervisioni((e) => e.filter((x) => x.id !== s.id))
                    if (s.ruolo !== 'supervisore') return
                    // Chi rinuncia non legge piu' la casella: esce dall'elenco e, se era quella
                    // aperta, si torna alla propria.
                    const restano = caselle.filter((c) => c.id !== s.casella_id || c.mia)
                    setCaselle(restano)
                    if (casellaId !== s.casella_id) return
                    const propria = restano.find((c) => c.mia && c.ambito === 'utente') ?? restano[0]
                    if (propria) scegliCasella(propria.id)
                    else setStato('assente')
                  }} />
              ))}
            </>
          )}

          {puo('posta.supervisione_concedi') && (
            <SupervisioniDaControllare escluse={supervisioniAttive.map((s) => s.id)} />
          )}

          {puo('posta.supervisiona') && (
            <RichiesteSupervisione aperta={(id) => void supervisioneAperta(id).catch(gestisci)} />
          )}

          {puo('posta.collegamenti_registro') && <RegistroCollegamenti />}
          {puo('posta.inoltri_controlla') && <InoltriInDeroga />}
        </section>

        <section className="posta-elenco">
          <div className="posta-barra">
            <span className="solo-stretto">
              <BottoneIcona icona={<IconaIndietro />} etichetta="Torna alle caselle"
                            onClick={() => setPannello('caselle')} />
            </span>
            <span className="solo-largo">
              <BottoneIcona icona={<IconaColonna />}
                            etichetta={latoChiuso ? 'Mostra caselle e cartelle' : 'Nascondi caselle e cartelle'}
                            aria-expanded={!latoChiuso} onClick={alternaLato} />
            </span>
            {/* Campo di ricerca della barra: l'etichetta e' l'aria-label, perche' .posta-barra
                dispone il comando su una riga sola, con l'icona sovrapposta al campo. */}
            <div className="cerca">
              <IconaCerca className="icona-campo" />
              <input type="search" value={cerca} onChange={(e) => setCerca(e.target.value)}
                     placeholder="Cerca: parole, mittente, oggetto" aria-label="Cerca fra i messaggi" />
              {sceltaRicerca && <SceltaAmbito valore={preferenze.ricerca} conCaselle={conCaselle} cambia={scegliAmbito} />}
            </div>
            {/* Aperte o archiviate; il cestino e' una voce delle cartelle, e li' la tendina non serve. */}
            {!nelCestino && (
              <select value={filtroStato} aria-label="Stato delle conversazioni" disabled={statoIndifferente}
                      title={statoIndifferente ? 'La ricerca in tutte le cartelle guarda aperte e archiviate' : undefined}
                      onChange={(e) => setFiltroStato(e.target.value as 'aperto' | 'archiviato')}>
                <option value="aperto">aperte</option>
                <option value="archiviato">archiviate</option>
              </select>
            )}
            <button type="button" className={`posta-filtro-non-lette${soloNonLette ? ' attivo' : ''}`}
                    aria-pressed={soloNonLette} onClick={() => setSoloNonLette((v) => !v)}
                    title="Mostra solo le conversazioni con messaggi da leggere">
              Solo non lette
            </button>
            <FiltroBandierina valore={filtroBandierina} cambia={setFiltroBandierina} />
            <FiltroEtichetta valore={filtroEtichetta} cambia={setFiltroEtichetta} etichette={etichette}
                             indirizzoDi={indirizzoDi} vistaUnica={vista === 'tutte'} />
            <BottoneIcona icona={<IconaAggiorna />} etichetta="Aggiorna" onClick={ricarica} />
            <BottoneIcona icona={<IconaAmministrazione />} etichetta="Elenco e lettura: ordine, vista, preferenze"
                          onClick={() => setPreferenzeAperte(true)} />
            {/* Anche dalla propria spenta (3/11/2026): il messaggio parte da un'altra casella, che lo dice «Da». */}
            {mittenti.length > 0 && (vista === 'tutte' || !!casella?.mia || (!!casella?.spenta && casella.ambito === 'utente')) && (
              <Bottone variante="primario" dimensione="piccolo" icona={<IconaScrivi />}
                       onClick={nuovaBozza}>
                Scrivi
              </Bottone>
            )}
          </div>

          {/* Con la colonna chiusa le cartelle, e il cestino, si scelgono qui. */}
          {latoChiuso && (vista === 'tutte' || cartelle.length > 0) && (
            <div className="posta-barra posta-barra-cartelle solo-largo">
              {nelCestino ? <IconaCestino /> : <IconaCartella />}
              <select value={nelCestino ? CESTINO : vista === 'tutte' ? '' : cartellaId ?? ''} aria-label="Cartella"
                      onChange={(e) => {
                        if (e.target.value === CESTINO) scegliCestino()
                        else if (vista === 'tutte') esciDalCestino()
                        else scegliCartella(e.target.value)
                      }}>
                {vista === 'tutte' && <option value="">Conversazioni</option>}
                {vista === 'casella' && inOrdineDiAlbero(cartelle).map((c) => (
                  <option key={c.id} value={c.id}>{c.madre_id ? '\u2003' : ''}{nomeCartella(c)}{c.non_letti > 0 ? ` (${c.non_letti})` : ''}</option>
                ))}
                <option value={CESTINO}>Cestino{cestino?.conversazioni ? ` (${cestino.conversazioni})` : ''}</option>
              </select>
            </div>
          )}

          {nelCestino && cestino && <p className="posta-nota">{posta.testoCestino(cestino)}</p>}
          {filtroSegni && vista === 'casella' && !nelCestino && (
            <p className="posta-nota">
              {filtroBandierina && filtroEtichetta ? 'Bandierina ed etichetta si cercano' : filtroBandierina
                ? 'La bandierina si cerca' : 'L’etichetta si cerca'} in tutte le cartelle della casella, fra le
              conversazioni {filtroStato === 'archiviato' ? 'archiviate' : 'aperte'}.
            </p>
          )}

          {cercaAttiva && (
            <NotaRicerca ambito={ambitoAttivo ?? 'cartella'} nelCestino={nelCestino} vistaUnica={vista === 'tutte'}
                         ordineDiverso={ordineDiverso} />
          )}

          {/* Selezione (541): «Seleziona tutte», l'ordine se non e' quello solito, «Segna tutte come lette». */}
          <SelezionaTutte selezione={selezione} righe={chiaviRighe.length}>
            {(ordineDiverso || vistaMessaggi) && !cercaAttiva && (
              <span className="posta-ordine-detto">
                {vistaMessaggi ? 'Per messaggi' : 'Per conversazioni'}
                {ordineDiverso && ` · ${elencoPosta.nomeOrdine(preferenze.ordine, preferenze.verso)}`}
                {ordinateRecenti && ` (le ${ordinateRecenti} più recenti)`}
              </span>
            )}
            {cartellaScelta && cartellaScelta.non_letti > 0 && scriveQui && !nelCestino && !filtroSegni && (
              <button type="button" className="posta-tutte-lette" onClick={() => void segnaTutteLette()}>
                Segna tutte come lette
              </button>
            )}
          </SelezionaTutte>
          {selezione.scelte.size > 0 && !io?.impersonazione && (
            <BarraSelezione quante={threadScelti.length} stato={statoElenco} inCorso={inBlocco}
                            cartelle={puoSpostare ? cartelle : []} cartellaAttuale={nelCestino || filtroSegni ? null : cartellaId}
                            etichette={etichette} pieno={vista === 'tutte' || !!casella?.mia}
                            soloPersonali={vista === 'casella' && (!!casella?.supervisione || casella?.livello === 'legge')}
                            esegui={(a) => void eseguiInBlocco(a)} svuota={selezione.svuota} />
          )}
          {esitoBlocco && <p className="posta-nota" role="status">{esitoBlocco}</p>}

          <div className="posta-scorrimento" onScroll={(e) => {
            const el = e.currentTarget
            if (cursore && !caricoElenco && el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
              void caricaPagina(cursore)
            }
          }}>
            {/* Le bozze del gestionale nella cartella «Bozze» (3/11/2026), prima dei messaggi della cartella. */}
            {bozzeQui.length > 0 && <BozzeNellaCartella voci={bozzeQui} apri={apriBozza} />}
            {/* Vista per messaggi (541): una riga per messaggio, le azioni sulla sua conversazione. */}
            {vistaMessaggi && righeMessaggi.map((m) => (
              <RigaMessaggio key={m.id} m={m} attiva={m.thread_id === threadId && (messaggioAperto ?? m.id) === m.id}
                             scelta={selezione.scelte.has(m.id)}
                             casellaDetta={vista === 'tutte' || fraCaselle ? indirizzoDi.get(m.casella_id) ?? 'casella' : null}
                             spunta={(
                               <SpuntaRiga scelta={selezione.scelte.has(m.id)} etichetta={`Scegli «${m.oggetto || 'senza oggetto'}»`}
                                           alterna={(intervallo) => selezione.alterna(m.id, intervallo)} />
                             )}
                             menu={(
                               <MenuSegni nellElenco thread={{ ...m, id: m.thread_id }}
                                          casella={caselle.find((c) => c.id === m.casella_id) ?? null} etichette={etichette}
                                          cambiati={(segni) => segniCambiati(m.thread_id, segni)}
                                          etichetteCambiate={etichetteCambiate} />
                             )}
                             trascina={puoSpostare ? (e) => iniziaTrascinamento(e, m.id, m.thread_id) : null}
                             apri={() => apriMessaggio(m)} />
            ))}
            {!vistaMessaggi && voci.map((v) => (
              <div key={v.id} className={`posta-voce-riga${selezione.scelte.has(v.id) ? ' selezionata' : ''}`} {...attributiRiga(v)}
                   draggable={puoSpostare} onDragStart={puoSpostare ? (e) => iniziaTrascinamento(e, v.id, v.id) : undefined}>
                <SpuntaRiga scelta={selezione.scelte.has(v.id)} etichetta={`Scegli «${v.oggetto || 'senza oggetto'}»`}
                            alterna={(intervallo) => selezione.alterna(v.id, intervallo)} />
                <button aria-current={v.id === threadId} data-thread={v.id}
                        className={`posta-voce${v.non_letti > 0 ? ' non-letto' : ''}`
                          + `${v.id === threadId ? ' attiva' : ''}`}
                        onClick={() => apri(v)}>
                  <span className="capo">
                    {v.non_letti > 0 && <span className="pallino" role="img" aria-label="da leggere" />}
                    {/* Bandierine (538): il colore con l'icona e il suo nome, per tutti e la propria. */}
                    <Bandierine segni={v} />
                    <span className="mittenti">
                      {v.partecipanti.map(posta.nomeBreve).join(', ') || '—'}
                      {v.messaggi > 1 && <span className="tenue"> ({v.messaggi})</span>}
                    </span>
                    <span className="quando">{posta.dataElenco(v.ultimo_il)}</span>
                  </span>
                  <span className="oggetto">
                    {/* Priorità alta (539). */}
                    {v.priorita_alta && <span className="posta-priorita" title="Priorità alta" aria-label="priorità alta">!</span>}
                    {v.oggetto || '(senza oggetto)'}
                  </span>
                  <span className="anteprima">{v.anteprima}</span>
                  {/* Sulle caselle di gruppo lo stato di lettura e' condiviso: chi l'ha aperto. */}
                  {v.aperto_da && (
                    <span className="anteprima">
                      aperto da {v.aperto_da.id === io?.id ? 'te' : `${v.aperto_da.nome} ${v.aperto_da.cognome}`}
                      {v.aperto_il && ` · ${posta.dataElenco(v.aperto_il)}`}
                    </span>
                  )}
                  {/* Cartella Inviata di una casella condivisa: chi ha mandato l'ultimo messaggio (2/11/2026). */}
                  {v.inviato_da && inInviata && (
                    <span className="anteprima">
                      inviato da {v.inviato_da.id === io?.id ? 'te' : `${v.inviato_da.nome} ${v.inviato_da.cognome}`}
                    </span>
                  )}
                  {nelCestino && v.cestinato_il && (
                    <span className="anteprima">
                      nel cestino dal {new Date(v.cestinato_il).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
                    </span>
                  )}
                  {/* Con la ricerca: in quali cartelle, e in quale casella se si cerca in tutte. */}
                  <DoveTrovato casella={fraCaselle ? indirizzoDi.get(v.casella_id) ?? null : null} cartelle={v.cartelle}
                               archiviata={v.stato === 'archiviato'} />
                  {v.etichette.length > 0 && (
                    <span className="coda"><Etichette etichette={v.etichette} massimo={3} /></span>
                  )}
                  {(v.ha_allegati || v.persona_id || vista === 'tutte') && (
                    <span className="coda">
                      {/* Nella vista unica ogni conversazione dice quale casella l'ha ricevuta. */}
                      {vista === 'tutte' && <Etichetta>{indirizzoDi.get(v.casella_id) ?? 'casella'}</Etichetta>}
                      {v.ha_allegati && <IconaGraffetta />}
                      {v.persona_id && <Etichetta tono="accento">scheda</Etichetta>}
                    </span>
                  )}
                  {/* Un messaggio della conversazione non e' partito (3/11/2026). */}
                  {!!v.non_inviati && <span className="coda"><EtichettaNonInviato /></span>}
                  {/* Provenienza (522): «esterno» e, in rosso, il falso CIDIS. */}
                  {v.provenienza && (
                    <span className="coda">
                      {v.provenienza === 'falsa_cidis'
                        ? <Etichetta tono="critico">falso CIDIS?</Etichetta>
                        : <Etichetta tono="attenzione">{v.provenienza === 'esterna' ? 'esterno' : 'esterno, non verificato'}</Etichetta>}
                    </span>
                  )}
                </button>
                {/* Il menu della riga sta accanto al bottone, non dentro: un bottone non ne contiene un altro. */}
                <MenuSegni nellElenco thread={v} casella={caselle.find((c) => c.id === v.casella_id) ?? null}
                           etichette={etichette} cambiati={(segni) => segniCambiati(v.id, segni)}
                           etichetteCambiate={etichetteCambiate} />
              </div>
            ))}
            {caricoElenco && <Attesa testo={cercaAttiva ? 'Ricerca in corso…' : 'Caricamento…'} />}
            {!caricoElenco && chiaviRighe.length === 0 && bozzeQui.length === 0 && (
              <div className="posta-nulla">
                <IconaVuoto />
                <p>{cercaAttiva
                  ? cursore ? 'Nessun messaggio trovato finora.' : 'Nessun messaggio trovato.'
                  : filtroSegni ? `Nessuna conversazione${soloNonLette ? ' da leggere' : ''} con ${
                    filtroBandierina && filtroEtichetta ? 'questa bandierina e questa etichetta'
                      : filtroBandierina ? (filtroBandierina === 'qualsiasi' ? 'una bandierina' : 'questa bandierina')
                        : 'questa etichetta'}.`
                  : soloNonLette ? 'Nessuna conversazione da leggere.'
                  : nelCestino ? 'Il cestino è vuoto.'
                    : vista === 'tutte' ? 'Nessun messaggio nelle tue caselle.' : 'Nessun messaggio in questa cartella.'}</p>
              </div>
            )}
            {/* La ricerca si ferma dopo qualche tratto di archivio: si continua a richiesta. */}
            {!caricoElenco && cercaAttiva && cursore && (
              <div className="posta-nuovo">
                <Bottone variante="discreto" dimensione="piccolo" onClick={() => void caricaPagina(cursore)}>
                  Cerca nei messaggi più vecchi
                </Bottone>
              </div>
            )}
          </div>
        </section>

        <section className="posta-lettura">
          {!aperto && !caricoThread && (
            <div className="posta-nulla">
              <IconaBusta />
              <p>Nessuna conversazione aperta.</p>
            </div>
          )}
          {caricoThread && <Attesa testo="Caricamento della conversazione…" />}
          {aperto && casellaAperta && (
            <Lettura
              thread={aperto}
              casella={casellaAperta}
              puoAgganciare={puo('posta.aggancia')}
              indietro={() => { setIntero(false); setPannello('elenco') }}
              intero={intero}
              alternaIntero={() => setIntero((v) => !v)}
              nuovaScheda={apriInNuovaScheda}
              rispondi={rispondi}
              inoltraComeAllegato={inoltraComeAllegato}
              cambiaStato={(nuovo) => void cambiaStatoThread(nuovo)}
              cartellaCambiata={cartellaCambiata}
              errore={gestisci}
              nonLetto={() => void segnaNonLetto()}
              letta={() => void segnaLetta()}
              riprova={riprovaInvio}
              modifica={modificaInvio}
              messaggioIniziale={messaggioAperto}
              recentiSopra={preferenze.conversazione !== 'recenti_sotto'}
              altriComandi={puoSpostare && casellaAperta?.id === casellaId
                ? <SpostaIn cartelle={cartelle.filter((c) => nelCestino || filtroSegni || c.id !== cartellaId)}
                            sposta={(id) => void spostaAperta(id)} />
                : null}
              aggancia={() => setAgganciamento(true)}
              etichette={etichette}
              segniCambiati={(segni) => segniCambiati(aperto.thread.id, segni)}
              etichetteCambiate={etichetteCambiate}
            />
          )}
        </section>
      </div>

      {compositore}

      {gestioneCartelle && casella && (
        <GestioneCartelle casellaId={casella.id} cartelle={cartelle} chiudi={() => setGestioneCartelle(false)}
                          cambiate={ricarica} />
      )}
      {preferenzeAperte && (
        <PreferenzePosta attuali={preferenze} chiudi={() => setPreferenzeAperte(false)}
                         tastiera={() => { setPreferenzeAperte(false); setAiutoTastiera(true) }}
                         salvate={(p) => { setPreferenze(p); setPreferenzeAperte(false) }} />
      )}
      {aiutoTastiera && <AiutoScorciatoie chiudi={() => setAiutoTastiera(false)} solaLettura={!!io?.impersonazione} />}

      {agganciamento && aperto && (
        <Collega
          threadId={aperto.thread.id}
          chiudi={() => setAgganciamento(false)}
          collegato={(personaId) => {
            setAgganciamento(false)
            setAperto((t) => (t ? { ...t, thread: { ...t.thread, persona_id: personaId } } : t))
            aggiornaVoce(aperto.thread.id, { persona_id: personaId })
          }}
        />
      )}
    </>
  )
}

/**
 * Le bozze in corso, nel lato della webmail: oggetto, destinatari o casella, chi le sta scrivendo
 * adesso e, su quelle condivise con te, chi ti ha invitato.
 */
function VociBozze({ voci, mostraCasella, apri }: {
  voci: bozze.VoceBozza[]; mostraCasella: boolean; apri: (id: string) => void
}) {
  return (
    <>
      {voci.map((b) => (
        <button key={b.id} type="button" className="posta-scelta posta-casella" onClick={() => apri(b.id)}>
          <span className="riga">
            {b.oggetto || '(senza oggetto)'}
            {b.risposta && <Etichetta>risposta</Etichetta>}
            {b.stato === 'in_invio' && <Etichetta tono="accento">in invio</Etichetta>}
          </span>
          <span className="indirizzo">{mostraCasella ? b.casella_indirizzo : b.a || 'nessun destinatario'}</span>
          {b.invitata_da && (
            <span className="indirizzo">invitata da {b.invitata_da.nome} {b.invitata_da.cognome}</span>
          )}
          {b.presenti.length > 0 && (
            <span className="indirizzo"><IconaGruppo /> scrive: {bozze.descriviPresenti(b.presenti)}</span>
          )}
          {!!b.invitati && (
            <span className="indirizzo">{b.invitati === 1 ? '1 collega invitato' : `${b.invitati} colleghi invitati`}</span>
          )}
        </button>
      ))}
    </>
  )
}

function VoceSupervisione({ supervisione, puoRevocare, revocata }: {
  supervisione: posta.Supervisione; puoRevocare: boolean; revocata: () => void
}) {
  const [errore, setErrore] = useState<string | null>(null)
  const { supervisore, titolare } = supervisione
  const scadenza = new Date(supervisione.scade_il).toLocaleDateString('it-IT')

  return (
    <div className="posta-supervisione">
      <div className="posta-supervisione-testo piccolo">
        <div>
          {supervisione.ruolo === 'supervisore'
            ? <>Leggi la casella {supervisione.casella_indirizzo}{titolare ? ` di ${titolare.nome} ${titolare.cognome}` : ''} fino al {scadenza}.</>
            : <>{supervisore.nome} {supervisore.cognome} legge la tua casella fino al {scadenza}.</>}
          {supervisione.solo_thread_id && <> Limitata a una sola conversazione.</>}
        </div>
        {supervisione.motivazione && <div className="tenue">«{supervisione.motivazione}»</div>}
        {errore && <div className="testo-critico">{errore}</div>}
      </div>
      {puoRevocare && (
        <Bottone variante="discreto" dimensione="piccolo"
                 onClick={() => void posta.revocaSupervisione(supervisione.id)
                   .then(revocata).catch((e) => setErrore(testoErrore(e)))}>
          Revoca
        </Bottone>
      )}
    </div>
  )
}

// --- supervisioni in corso, per chi le concede ---------------------------------------------------

/**
 * Le supervisioni in corso su tutte le caselle, per chi le concede e le revoca: il Referente privacy
 * e il DPO (`posta.supervisione_concedi`; decisione di Davide del 31/10/2026). Prima le vedeva solo
 * chi ne era parte, e il DPO le revocava solo dall'API. Quelle in cui chi guarda e' supervisore o
 * titolare stanno gia' nell'elenco di sopra (`escluse`). La revoca chiede conferma: avvisa titolare
 * e supervisore e non si annulla. Quella sulla propria casella non la revoca il titolare.
 */
function SupervisioniDaControllare({ escluse = [] }: { escluse?: string[] }) {
  const { io } = useSessione()
  const [elenco, setElenco] = useState<posta.SupervisioneInCorso[] | null>(null)
  const [daRevocare, setDaRevocare] = useState<posta.SupervisioneInCorso | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  const carica = useCallback(() => {
    posta.elencaSupervisioniInCorso().then(setElenco).catch((e) => setErrore(testoErrore(e)))
  }, [])
  useEffect(carica, [carica])

  async function revoca() {
    if (!daRevocare) return
    setErrore(null); setInCorso(true)
    try {
      await posta.revocaSupervisione(daRevocare.id)
      setDaRevocare(null)
      carica()
    } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(false) }
  }

  const voci = (elenco ?? []).filter((s) => !escluse.includes(s.id))
  return (
    <>
      <div className="posta-titoletto">Supervisioni in corso su tutte le caselle</div>
      {errore && <p className="posta-nota testo-critico">{errore}</p>}
      {elenco && voci.length === 0 && <p className="posta-nota">Nessuna supervisione in corso.</p>}
      {voci.map((s) => (
        <div key={s.id} className="posta-supervisione">
          <div className="posta-supervisione-testo piccolo">
            <div>
              {s.supervisore.nome} {s.supervisore.cognome} legge la casella {s.casella_indirizzo}
              {s.titolare ? ` di ${s.titolare.nome} ${s.titolare.cognome}` : ''} fino al{' '}
              {new Date(s.scade_il).toLocaleDateString('it-IT')}.
              {s.solo_thread_id && <> Limitata a una sola conversazione.</>}
            </div>
            {s.motivazione && <div className="tenue">«{s.motivazione}»</div>}
            {s.concessa_da && <div className="tenue">Concessa da {s.concessa_da.nome} {s.concessa_da.cognome}.</div>}
          </div>
          {s.titolare?.id !== io?.id && (
            <Bottone variante="discreto" dimensione="piccolo" onClick={() => { setErrore(null); setDaRevocare(s) }}>
              Revoca
            </Bottone>
          )}
        </div>
      ))}
      {daRevocare && (
        <Modale titolo="Revoca la supervisione" chiudi={() => setDaRevocare(null)}
          azioni={
            <>
              <Bottone variante="discreto" onClick={() => setDaRevocare(null)}>Annulla</Bottone>
              <Bottone variante="critico" inCorso={inCorso} onClick={() => void revoca()}>Revoca</Bottone>
            </>
          }>
          {errore && <Avviso tipo="errore">{errore}</Avviso>}
          <p>
            {daRevocare.supervisore.nome} {daRevocare.supervisore.cognome} non leggerà più la casella{' '}
            {daRevocare.casella_indirizzo}. Il titolare e il supervisore ricevono un avviso; la revoca
            resta nel registro e non si annulla.
          </p>
        </Modale>
      )}
    </>
  )
}

// --- richiesta di supervisione -------------------------------------------------------------------

const giorniATesto = (n: number) => (n === 1 ? 'un giorno' : `${n} giorni`)
const dataBreve = (v: string | null) => (v ? new Date(v).toLocaleDateString('it-IT') : '—')

/**
 * Supervisione di una casella altrui chiesta dalla pagina (adeguamento 3 del LEGGIMI della webmail,
 * docs/legale/webmail/). Chi ha `posta.supervisiona` sceglie la casella e scrive motivazione e
 * durata; il Referente privacy (o il DPO) decide nella pagina «Approvazioni»; concessa, da qui si apre la lettura per la
 * durata approvata. Le regole le applica l'API: la pagina le rende visibili.
 */
function RichiesteSupervisione({ aperta }: { aperta: (casellaId: string) => void }) {
  const [richieste, setRichieste] = useState<posta.RichiestaSupervisione[]>([])
  const [scelta, setScelta] = useState<{ massimo_giorni: number; caselle: posta.CasellaSorvegliabile[] } | null>(null)
  const [modulo, setModulo] = useState(false)
  const [daAprire, setDaAprire] = useState<posta.RichiestaSupervisione | null>(null)
  const [errore, setErrore] = useState<string | null>(null)

  const carica = useCallback(() => {
    Promise.all([posta.elencaRichiesteSupervisione(), posta.caselleSorvegliabili()])
      .then(([r, c]) => { setRichieste(r); setScelta(c) })
      .catch((e) => setErrore(testoErrore(e)))
  }, [])
  useEffect(carica, [carica])

  return (
    <>
      <div className="posta-titoletto">Richieste di supervisione</div>
      {errore && <p className="posta-nota testo-critico">{errore}</p>}
      {richieste.map((r) => (
        <div key={r.id} className="posta-supervisione">
          <div className="posta-supervisione-testo piccolo">
            <div>
              {r.casella_indirizzo ?? 'Casella non più presente'}
              {r.titolare && ` (${r.titolare.nome} ${r.titolare.cognome})`}
              {r.giorni && `, ${giorniATesto(r.giorni)}`}
            </div>
            <div className="tenue">
              {r.stato === 'in_attesa' && <>In attesa del Referente privacy, scade il {dataBreve(r.scade_il)}.</>}
              {r.stato === 'approvata' && <>Approvata: da aprire entro il {dataBreve(r.scade_il)}.</>}
              {r.stato === 'respinta' && <>Respinta{r.nota_decisione ? `: «${r.nota_decisione}»` : '.'}</>}
            </div>
          </div>
          {r.stato === 'approvata' && (
            <Bottone variante="primario" dimensione="piccolo" onClick={() => setDaAprire(r)}>Apri</Bottone>
          )}
        </div>
      ))}
      <Bottone variante="discreto" dimensione="piccolo" icona={<IconaOcchio />}
               disabled={!scelta} onClick={() => setModulo(true)}>
        Chiedi una supervisione
      </Bottone>

      {modulo && scelta && (
        <ModuloSupervisione scelta={scelta} chiudi={() => setModulo(false)}
                            inviata={() => { setModulo(false); carica() }} />
      )}
      {daAprire && (
        <ConfermaSupervisione richiesta={daAprire} massimo={scelta?.massimo_giorni ?? 30}
          chiudi={() => setDaAprire(null)}
          aperta={() => { const id = daAprire.casella_id; setDaAprire(null); carica(); aperta(id) }} />
      )}
    </>
  )
}

/** La richiesta: casella, motivazione e durata. Decide il Referente privacy (o il DPO), in «Approvazioni». */
function ModuloSupervisione({ scelta, chiudi, inviata }: {
  scelta: { massimo_giorni: number; caselle: posta.CasellaSorvegliabile[] }
  chiudi: () => void
  inviata: () => void
}) {
  const [casella, setCasella] = useState('')
  const [motivazione, setMotivazione] = useState('')
  const [giorni, setGiorni] = useState(String(Math.min(7, scelta.massimo_giorni)))
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  const n = Number(giorni)
  const valida = !!casella && motivazione.trim().length >= 20
    && Number.isInteger(n) && n >= 1 && n <= scelta.massimo_giorni

  async function invia() {
    setErrore(null); setInCorso(true)
    try {
      await posta.chiediSupervisione({ casella_id: casella, motivazione: motivazione.trim(), giorni: n })
      inviata()
    } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(false) }
  }

  return (
    <Modale titolo="Chiedi una supervisione" chiudi={chiudi}
      azioni={
        <>
          <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
          <Bottone variante="primario" inCorso={inCorso} disabled={!valida} onClick={() => void invia()}>
            Invia la richiesta
          </Bottone>
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <p className="piccolo tenue">
        La decide il Referente privacy (o il DPO) nella pagina «Approvazioni». Aperta la supervisione, il titolare riceve un
        avviso subito e a ogni conversazione che apri; ogni apertura resta nel registro.
      </p>
      {scelta.caselle.length === 0
        ? <Vuoto testo="Nessuna casella personale di altri operatori." />
        : (
          <>
            <Scelta etichetta="Casella" obbligatorio valore={casella} cambia={setCasella}
                    vuota="Scegli la casella"
                    opzioni={scelta.caselle.map((c) => ({
                      valore: c.id, etichetta: `${c.titolare.cognome} ${c.titolare.nome} · ${c.indirizzo}`,
                    }))} />
            <AreaTesto etichetta="Motivazione" obbligatorio righe={4} maxLength={1000}
                       valore={motivazione} cambia={setMotivazione}
                       aiuto="Almeno 20 caratteri. La leggono il Referente privacy e il titolare della casella." />
            <Campo tipo="number" etichetta="Durata in giorni" obbligatorio valore={giorni} cambia={setGiorni}
                   min={1} max={scelta.massimo_giorni} passi={1}
                   aiuto={`Al massimo ${giorniATesto(scelta.massimo_giorni)}, dall’apertura.`} />
          </>
        )}
    </Modale>
  )
}

/** Conferma prima di aprire: e' il momento in cui il titolare viene avvisato. */
function ConfermaSupervisione({ richiesta: r, massimo, chiudi, aperta }: {
  richiesta: posta.RichiestaSupervisione; massimo: number; chiudi: () => void; aperta: () => void
}) {
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  // Le richieste fatte da qui portano la durata approvata; quelle senza valgono fino al massimo.
  const giorni = r.giorni ?? massimo

  async function apri() {
    setErrore(null); setInCorso(true)
    try {
      await posta.apriSupervisione({
        casella_id: r.casella_id, approvazione_id: r.id, motivazione: r.motivazione, giorni,
      })
      aperta()
    } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(false) }
  }

  return (
    <Modale titolo="Apri la supervisione" chiudi={chiudi}
      azioni={
        <>
          <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
          <Bottone variante="primario" inCorso={inCorso} onClick={() => void apri()}>
            Apri la supervisione
          </Bottone>
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <p>
        Leggerai la casella {r.casella_indirizzo}
        {r.titolare && ` di ${r.titolare.nome} ${r.titolare.cognome}`} per {giorniATesto(giorni)}, in
        sola lettura.
      </p>
      <p className="piccolo tenue">
        Il titolare riceve un avviso ora e a ogni conversazione che apri, e vede la motivazione:
      </p>
      <p className="piccolo">«{r.motivazione}»</p>
    </Modale>
  )
}

// --- cestino (535) --------------------------------------------------------------------------------

/** Valore della voce «Cestino» nella tendina delle cartelle a colonna chiusa: non e' un identificativo. */
const CESTINO = 'cestino'

const TITOLO_CESTINO_SERVER = 'La cartella cestino letta dal server di posta. Il cestino del gestionale, '
  + 'con le conversazioni spostate con «Sposta nel cestino», è la voce «Cestino».'

/**
 * Il nome della cartella nella colonna. Quella di tipo cestino viene dal server (la cartella Trash di
 * IMAP, o il Cestino della vecchia casella caricata da Maildir): accanto alla voce «Cestino» del
 * gestionale due «Cestino» sarebbero un doppione, e non si svuotano allo stesso modo.
 */
function nomeCartella(c: posta.Cartella): string {
  return c.tipo === 'cestino' ? 'Cestino del server' : c.nome
}

/** La voce «Cestino» della colonna, con il numero delle conversazioni che contiene. */
function VoceCestino({ attiva, cestino, scegli }: {
  attiva: boolean; cestino: posta.RiepilogoCestino | null; scegli: () => void
}) {
  return (
    <button className={`posta-scelta${attiva ? ' attiva' : ''}`} aria-current={attiva} onClick={scegli}>
      <IconaCestino />
      <span className="posta-cartella-nome">Cestino</span>
      {!!cestino?.conversazioni && (
        <span className="conteggio" aria-label={cestino.conversazioni === 1
          ? '1 conversazione nel cestino' : `${cestino.conversazioni} conversazioni nel cestino`}>
          {cestino.conversazioni}
        </span>
      )}
    </button>
  )
}

// --- conversazione aperta ------------------------------------------------------------------------

function Lettura({
  thread, casella, puoAgganciare, indietro, intero, alternaIntero, nuovaScheda, rispondi, inoltraComeAllegato, cambiaStato,
  nonLetto, aggancia, etichette, segniCambiati, etichetteCambiate, cartellaCambiata, errore, letta, messaggioIniziale,
  recentiSopra, altriComandi, riprova, modifica,
}: {
  thread: posta.ThreadAperto
  casella: posta.CasellaWebmail
  puoAgganciare: boolean
  indietro: () => void
  /** Lettura a tutta pagina, e il comando che la accende e la spegne. */
  intero: boolean
  alternaIntero: () => void
  nuovaScheda: () => void
  rispondi: (tipo: 'rispondi' | 'tutti' | 'inoltra', messaggio?: posta.Messaggio) => void
  /** «Inoltra come allegato» (539): l'ultimo messaggio come file .eml. */
  inoltraComeAllegato: (messaggio?: posta.Messaggio) => Promise<void>
  /** «Archivia», «Sposta nel cestino», «Ripristina»: lo stato nuovo della conversazione. */
  cambiaStato: (stato: posta.StatoThread) => void
  /** «È indesiderata» o «Non è indesiderata» (542): i messaggi ricevuti hanno cambiato cartella. */
  cartellaCambiata: () => void
  errore: (e: unknown) => void
  nonLetto: () => void
  aggancia: () => void
  /** Bandierine ed etichette (538): le etichette della vista e che cosa fare quando cambiano. */
  etichette: posta.EtichettaPosta[]
  segniCambiati: (segni: posta.SegniThread) => void
  etichetteCambiate: (cambio: CambioEtichette) => void
  /** «Segna come letta» (541), al posto di «Segna come non letta» finche' c'e' da leggere. */
  letta: () => void
  /** Dalla vista per messaggi (541): il messaggio da aprire al posto dell'ultimo. */
  messaggioIniziale: string | null
  /** Il messaggio piu' recente in alto (predefinito dal 4/11/2026) o in fondo, secondo «Elenco e lettura». */
  recentiSopra: boolean
  /** Comandi in piu' nella riga dei comandi: «Sposta in…» (541). */
  altriComandi: ReactNode
  /** «Riprova» e «Modifica» su un messaggio non partito (3/11/2026). */
  riprova: (m: posta.Messaggio) => Promise<string | null | 'inviato'>
  modifica: (m: posta.Messaggio) => Promise<void>
}) {
  const [espansi, setEspansi] = useState<string[]>([])
  const ultimoId = thread.messaggi[thread.messaggi.length - 1]?.id
  const daLeggere = thread.messaggi.some((m) => !m.letto_il)

  // All'apertura resta espanso l'ultimo messaggio, o quello scelto nella vista per messaggi; gli altri a richiesta.
  useEffect(() => {
    const iniziale = messaggioIniziale && thread.messaggi.some((m) => m.id === messaggioIniziale) ? messaggioIniziale : null
    setEspansi(iniziale ? [iniziale] : ultimoId ? [ultimoId] : [])
    if (iniziale) requestAnimationFrame(() => document.getElementById(`messaggio-${iniziale}`)?.scrollIntoView({ block: 'start' }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread.thread.id, ultimoId, messaggioIniziale])

  const alterna = (id: string) => setEspansi((e) =>
    (e.includes(id) ? e.filter((x) => x !== id) : [...e, id]))
  // Dal riquadro degli allegati (2/11/2026): apre il messaggio e ci porta la pagina.
  const vaiAlMessaggio = (id: string) => {
    setEspansi((e) => (e.includes(id) ? e : [...e, id]))
    requestAnimationFrame(() => document.getElementById(`messaggio-${id}`)?.scrollIntoView({ block: 'start' }))
  }
  // I messaggi arrivano dal piu' vecchio, e rispondere e inoltrare dai comandi della conversazione restano
  // sull'ultimo arrivato. A video il piu' recente sta in alto (Osservazioni 6 di Carmem, 4/11/2026: con quindici
  // risposte l'ultima finiva in fondo); chi preferisce l'ordine di prima lo sceglie in «Elenco e lettura».
  const inOrdine = useMemo(() => (recentiSopra ? [...thread.messaggi].reverse() : thread.messaggi),
    [thread.messaggi, recentiSopra])
  const conAllegati = inOrdine.filter((m) => m.allegati.length > 0)
  const { io } = useSessione()
  // Sola lettura su una casella condivisa (504): niente comandi che cambiano la casella per gli altri.
  const solaLettura = casella.livello === 'legge'
  // Stato della conversazione (archivio, cestino): mai con la sola lettura, in supervisione o
  // impersonando (524). L'API lo rifiuta comunque; qui il comando non si propone.
  const cambiaLoStato = !solaLettura && !casella.supervisione && !casella.spenta && !io?.impersonazione
  // Chi rispedisce un messaggio non partito: chi scrive dalla casella, non impersonando (3/11/2026). Sulla
  // casella spenta si riapre con «Modifica» e si sceglie un'altra casella in «Da».
  const rispedisce = (casella.mia || (!!casella.spenta && casella.ambito === 'utente')) && !io?.impersonazione
  const statoDetto = posta.etichettaStato(thread.thread.stato)

  return (
    <>
      {/* Non e' una lettura per conto d'altri: un avviso informativo, non quello della supervisione. */}
      {casella.spenta && casella.ambito === 'utente' && (
        <Avviso tipo="informazione">
          Casella spenta: non spedisce. Si consulta soltanto: per rispondere scrivi da un’altra casella.
        </Avviso>
      )}
      {solaLettura && (
        <Avviso tipo="informazione">
          {casella.progetto
            ? <>Casella del progetto «{casella.progetto.titolo}»: hai la sola lettura.</>
            : <>Su questa casella hai la sola lettura.</>}
          {' '}Non rispondi e non cambi lo stato delle conversazioni, che resta quello di chi vi scrive.
        </Avviso>
      )}
      {casella.supervisione && (
        <div className="posta-altrui">
          <IconaOcchio />
          <div>
            Casella di un altro operatore, letta in forza di una supervisione a termine.
            L’apertura di ogni conversazione è registrata come evento di sicurezza e il titolare
            della casella ne è informato. La supervisione
            {casella.supervisione && <> scade il {new Date(casella.supervisione.scade_il).toLocaleDateString('it-IT')}</>}
            {' '}e da questa casella non si può scrivere.
          </div>
        </div>
      )}

      <header className="posta-capo">
        <div className="colonna">
          <div className="riga">
            <span className="solo-stretto">
              <BottoneIcona icona={<IconaIndietro />} etichetta="Torna all’elenco"
                            onClick={indietro} />
            </span>
            <h2 className="posta-oggetto">{thread.thread.oggetto || '(senza oggetto)'}</h2>
            {statoDetto && <Etichetta>{statoDetto}</Etichetta>}
            {thread.thread.persona_id && <Etichetta tono="accento">collegata a una scheda</Etichetta>}
            <span className="posta-capo-comandi solo-largo">
              <BottoneIcona icona={intero ? <IconaRiduci /> : <IconaIngrandisci />}
                            etichetta={intero ? 'Torna alla posta' : 'Leggi a tutta pagina'} onClick={alternaIntero} />
              <BottoneIcona icona={<IconaNuovaScheda />} etichetta="Apri in una nuova scheda" onClick={nuovaScheda} />
            </span>
          </div>
          {(thread.thread.bandierina || thread.thread.bandierina_personale || thread.thread.etichette.length > 0) && (
            <div className="riga segni-riga">
              <Bandierine segni={thread.thread} />
              <Etichette etichette={thread.thread.etichette} />
            </div>
          )}

          <div className="riga">
            {casella.mia && (
              <>
                <Bottone dimensione="piccolo" icona={<IconaRispondi />}
                         onClick={() => rispondi('rispondi')}>Rispondi</Bottone>
                <Bottone dimensione="piccolo" icona={<IconaRispondiATutti />}
                         onClick={() => rispondi('tutti')}>Rispondi a tutti</Bottone>
                <Bottone dimensione="piccolo" icona={<IconaInoltra />}
                         onClick={() => rispondi('inoltra')}>Inoltra</Bottone>
                <BottoneInoltraComeAllegato inoltra={() => inoltraComeAllegato()} />
              </>
            )}
            {/* Nel cestino si ripristina e basta; fuori, archivio e cestino (535). */}
            {cambiaLoStato && (thread.thread.stato === 'cestinato'
              ? (
                <Bottone variante="discreto" dimensione="piccolo" icona={<IconaRipristina />}
                         onClick={() => cambiaStato('aperto')}>Ripristina</Bottone>
              )
              : (
                <>
                  <Bottone variante="discreto" dimensione="piccolo" icona={<IconaArchivia />}
                           onClick={() => cambiaStato(thread.thread.stato === 'archiviato' ? 'aperto' : 'archiviato')}>
                    {thread.thread.stato === 'archiviato'
                      ? 'Riporta fra le conversazioni aperte'
                      : 'Archivia'}
                  </Bottone>
                  <Bottone variante="discreto" dimensione="piccolo" icona={<IconaCestino />}
                           onClick={() => cambiaStato('cestinato')}>Sposta nel cestino</Bottone>
                </>
              ))}
            {/* Letta e da leggere cambiano la casella: non con la sola lettura, in supervisione o impersonando, dove
                l'API li rifiuta (esigiScrittura, 524). */}
            {cambiaLoStato && (daLeggere
              ? <Bottone variante="discreto" dimensione="piccolo" icona={<IconaBusta />} onClick={letta}>Segna come letta</Bottone>
              : <Bottone variante="discreto" dimensione="piccolo" icona={<IconaBusta />}
                         onClick={nonLetto}>Segna come non letta</Bottone>)}
            {altriComandi}
            {/* «È indesiderata» / «Non è indesiderata» (542): lo propone l'API a chi ha l'accesso pieno. */}
            <ComandoIndesiderata threadId={thread.thread.id} attivo={cambiaLoStato && thread.thread.stato !== 'cestinato'}
                                 spostata={cartellaCambiata} errore={errore} />
            {/* Collegare cambia la casella: non dove l'API lo rifiuta (sola lettura, supervisione, impersonando). */}
            {puoAgganciare && cambiaLoStato && (
              <Bottone variante="discreto" dimensione="piccolo" icona={<IconaAggancia />}
                       onClick={aggancia}>Collega a una scheda</Bottone>
            )}
            {/* Anche con la sola lettura e in supervisione: le proprie si mettono sempre (538). */}
            <MenuSegni thread={thread.thread} casella={casella} etichette={etichette}
                       cambiati={segniCambiati} etichetteCambiate={etichetteCambiate} />
          </div>
        </div>
      </header>

      <div className="posta-contenuto">
        {/* Con piu' messaggi, in cima tutti gli allegati della conversazione, per messaggio: chi cercava
            un file doveva aprire i messaggi uno a uno (richiesta del 2/11/2026). */}
        {thread.messaggi.length > 1 && conAllegati.length > 0 && (
          <section className="posta-allegati-conversazione" aria-label="Allegati nella conversazione">
            <div className="posta-titoletto">
              <IconaGraffetta /> Allegati nella conversazione
              ({conAllegati.reduce((n, m) => n + m.allegati.length, 0)})
            </div>
            {conAllegati.map((m) => (
              <div key={m.id} className="posta-allegati-del-messaggio">
                <button type="button" className="posta-vai-messaggio" onClick={() => vaiAlMessaggio(m.id)}>
                  {posta.nomeBreve(m.da)} · {posta.dataEstesa(m.data)}
                </button>
                <div className="posta-allegati-elenco">
                  {m.allegati.map((a) => (
                    <BottoneAllegato key={a.id} allegato={a} postaId={m.id}
                                     gestisce={casella.mia && m.direzione === 'uscita'} />
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}
        {inOrdine.map((m) => (
          <MessaggioLetto key={m.id} messaggio={m} espanso={espansi.includes(m.id)}
                          alterna={() => alterna(m.id)} gestisce={casella.mia && m.direzione === 'uscita'}
                          nonInviato={m.stato_invio === 'errore' ? {
                            comandi: rispedisce ? {
                              riprova: () => riprova(m),
                              modifica: () => modifica(m),
                            } : null,
                            // La casella spenta riapre e basta: «Riprova» da li' non partirebbe.
                            soloModifica: !!casella.spenta,
                            perche: casella.supervisione ? 'Da una casella in supervisione non si spedisce.'
                              : solaLettura ? 'Hai la sola lettura: lo rispedisce chi scrive dalla casella.' : null,
                          } : null}
                          inoltra={casella.mia ? {
                            semplice: () => rispondi('inoltra', m), comeAllegato: () => inoltraComeAllegato(m),
                          } : null} />
        ))}
      </div>
    </>
  )
}

/** Fascia della provenienza sopra un messaggio arrivato (522). */
function FasciaProvenienza({ provenienza }: { provenienza: posta.Provenienza }) {
  const f = posta.FASCE_PROVENIENZA[provenienza]
  return (
    <div className={`posta-provenienza posta-provenienza-${f.tono}`} role={f.tono === 'critico' ? 'alert' : 'note'}>
      <strong>{f.titolo}</strong>
      <span>{f.testo}</span>
    </div>
  )
}

function MessaggioLetto({ messaggio, espanso, alterna, gestisce, inoltra, nonInviato }: {
  messaggio: posta.Messaggio; espanso: boolean; alterna: () => void
  /** Messaggio non partito (3/11/2026): «Non inviato», il motivo, «Riprova» e «Modifica». */
  nonInviato?: {
    comandi: { riprova: () => Promise<string | null | 'inviato'>; modifica: () => Promise<void> } | null
    soloModifica: boolean
    perche: string | null
  } | null
  /** Messaggio spedito da una casella da cui si scrive: i collegamenti si vedono e si revocano (509). */
  gestisce: boolean
  /** «Inoltra» e «Inoltra come allegato» di questo messaggio, per chi scrive dalla casella (3/11/2026). */
  inoltra: { semplice: () => void; comeAllegato: () => Promise<void> } | null
}) {
  const [immagini, setImmagini] = useState(false)
  const corpo = useMemo(
    () => (messaggio.html ? posta.ripulisciHtml(messaggio.html, immagini) : null),
    [messaggio.html, immagini])
  // Una pagina per contenuto: con un nonce nuovo a ogni disegno l'iframe si ricaricherebbe da capo.
  const pagina = useMemo(() => (corpo ? posta.paginaMessaggio(corpo.html, immagini) : ''), [corpo, immagini])
  const cornice = useRef<HTMLIFrameElement>(null)
  const collegamento = useCollegamentoToccato(cornice)
  useAltezzaCornice(cornice)
  const allegati = messaggio.allegati.length
  const testoAllegati = allegati === 1 ? '1 allegato' : `${allegati} allegati`
  /** Dalla graffetta: apre il messaggio, se e' chiuso, e porta la pagina e il fuoco sugli allegati. */
  function vaiAgliAllegati() {
    if (!espanso) alterna()
    requestAnimationFrame(() => {
      const riquadro = document.getElementById(`allegati-${messaggio.id}`)
      riquadro?.scrollIntoView({ block: 'nearest' })
      riquadro?.focus({ preventScroll: true })
    })
  }

  return (
    <div id={`messaggio-${messaggio.id}`} className={`posta-messaggio${espanso ? '' : ' chiuso'}`}>
      <header>
        <button type="button" className="posta-alterna" onClick={alterna} aria-expanded={espanso}
                title={posta.nomeEIndirizzo(messaggio.da)}>
          <span className="testo">
            <span className="chi">
              {/* Aperto, l'indirizzo completo di chi scrive sta sotto, in Indirizzi, dove si seleziona e si
                  copia (2/11/2026): il testo di un pulsante non si seleziona. */}
              {posta.nomeBreve(messaggio.da)}
              {messaggio.priorita_alta && <span className="posta-priorita" title="Priorità alta" aria-label="priorità alta">!</span>}
              {/* Da una casella di gruppo o di progetto: chi l'ha mandato (2/11/2026), anche chiuso. */}
              {messaggio.inviato_da && (
                <span className="posta-inviato-da">
                  inviato da {messaggio.inviato_da.nome} {messaggio.inviato_da.cognome}
                </span>
              )}
              {/* Ogni messaggio dice se viene da CIDIS, anche chiuso (2/11/2026). */}
              <EtichettaProvenienza messaggio={messaggio} />
              {/* Non partito (3/11/2026), anche chiuso. */}
              {nonInviato && <EtichettaNonInviato errore={messaggio.errore} />}
            </span>
            {!espanso && <span className="dettaglio">{(messaggio.testo ?? '').slice(0, 120)}</span>}
          </span>
          <span className="quando">{posta.dataEstesa(messaggio.data)}</span>
        </button>
        {/* La graffetta con il numero, anche a messaggio chiuso, porta agli allegati (4/11/2026): e' un comando a
            se', fuori da quello che apre e chiude il messaggio. */}
        {allegati > 0 && (
          <button type="button" className="posta-graffetta" onClick={vaiAgliAllegati}
                  title={`${testoAllegati}: vai agli allegati`} aria-label={`${testoAllegati}: vai agli allegati`}>
            <IconaGraffetta /> {allegati}
          </button>
        )}
      </header>
      {nonInviato && (
        <NonInviato messaggio={messaggio} perche={nonInviato.perche}
                    comandi={nonInviato.comandi && (nonInviato.soloModifica
                      ? { modifica: nonInviato.comandi.modifica, riprova: null }
                      : nonInviato.comandi)} />
      )}
      {espanso && <Indirizzi da={messaggio.da} a={messaggio.a} cc={messaggio.cc} />}
      {/* Gli allegati subito sotto Da, A e Cc, prima dei comandi e degli avvisi (4/11/2026, Osservazioni 6 di
          Carmem): stavano sotto «Esterno a CIDIS» e non si vedevano, e chi cercava il PDF allegato premeva il
          comando che salva in PDF il messaggio. Uno per riga: nome, peso e «Scarica». */}
      {espanso && allegati > 0 && (
        <section id={`allegati-${messaggio.id}`} tabIndex={-1} className="posta-allegati posta-allegati-in-cima"
                 aria-label={`Allegati del messaggio: ${testoAllegati}`}>
          <span className="posta-allegati-conto"><IconaGraffetta /> {testoAllegati}</span>
          {messaggio.allegati.map((a) => (
            <BottoneAllegato key={a.id} allegato={a} postaId={messaggio.id} gestisce={gestisce} />
          ))}
        </section>
      )}
      {/* Stampa, PDF dalla finestra di stampa, .eml ricomposto dall'API (2/11/2026). */}
      {espanso && (
        <ComandiMessaggio percorsoEml={posta.percorsoEml(messaggio.id)} percorsoOriginale={percorsoOriginale(messaggio.id)}
          messaggio={{
          oggetto: messaggio.oggetto, da: messaggio.da, a: messaggio.a, cc: messaggio.cc, data: messaggio.data,
          inviatoDa: messaggio.inviato_da, allegati: messaggio.allegati, testo: messaggio.testo, html: messaggio.html,
        }} prima={inoltra && (
          // Inoltro di questo messaggio, con la sua citazione e i suoi allegati (3/11/2026, come in Roundcube).
          <>
            <Bottone variante="discreto" dimensione="piccolo" icona={<IconaInoltra />} onClick={inoltra.semplice}>
              Inoltra
            </Bottone>
            <BottoneInoltraComeAllegato discreto inoltra={inoltra.comeAllegato} />
          </>
        )} />
      )}

      {/* Conferme di lettura (539): chieste sul messaggio arrivato, ricevute su quello inviato. */}
      {espanso && <ConfermeRicevute messaggio={messaggio} />}
      {espanso && messaggio.provenienza && messaggio.direzione === 'entrata' && (
        <FasciaProvenienza provenienza={messaggio.provenienza} />
      )}
      {espanso && messaggio.direzione === 'entrata' && <AvvisoConferma messaggio={messaggio} />}
      {espanso && messaggio.scadenza && (
        <BloccoScadenza postaId={messaggio.id} scadenza={messaggio.scadenza} gestisce={gestisce} />
      )}
      {espanso && (
        <div className="posta-contenuto">
          {corpo ? (
            <>
              {corpo.immaginiBloccate > 0 && !immagini && (
                <div className="posta-blocco-immagini">
                  <IconaImmagine />
                  <span>
                    {corpo.immaginiBloccate === 1
                      ? '1 immagine remota non è stata caricata'
                      : `${corpo.immaginiBloccate} immagini remote non sono state caricate`}:
                    il caricamento segnalerebbe al mittente l’apertura del messaggio.
                  </span>
                  <Bottone variante="discreto" dimensione="piccolo"
                           onClick={() => setImmagini(true)}>Mostra le immagini</Bottone>
                </div>
              )}
              {/* Corpo di provenienza esterna: srcdoc gia' ripulito, sandbox con i soli script. Senza
                  allow-same-origin il documento del mittente ha un'origine opaca: niente archiviazione
                  locale, niente /api con il cookie dell'operatore, anche se la ripulitura cedesse. Gira
                  solo lo script del gestionale (CSP con nonce), che manda alla pagina il collegamento
                  toccato; senza allow-popups e allow-top-navigation nessun collegamento si apre da se'. */}
              <iframe ref={cornice} className="posta-html" sandbox="allow-scripts"
                      title={`Corpo del messaggio del ${posta.dataEstesa(messaggio.data)}`}
                      srcDoc={pagina} />
              {collegamento.scelto && <ConfermaApertura verifica={collegamento.scelto} chiudi={collegamento.chiudi} />}
            </>
          ) : (
            <TestoConCollegamenti testo={messaggio.testo || '(messaggio senza testo)'} />
          )}
        </div>
      )}
    </div>
  )
}

function BottoneAllegato({ allegato, postaId, gestisce }: {
  allegato: posta.Allegato; postaId: string; gestisce: boolean
}) {
  const [errore, setErrore] = useState<string | null>(null)
  const [registro, setRegistro] = useState(false)
  const [avviso, setAvviso] = useState(false)
  const pulito = allegato.antivirus === 'pulito'
  const infetto = allegato.antivirus === 'infetto'
  const k = allegato.collegamento

  // La finestra si apre subito, nel gesto dell'utente: dopo l'attesa della risposta il browser la
  // tratterebbe come un popup non richiesto.
  async function scarica(nonControllato = false) {
    const finestra = window.open('', '_blank')
    try {
      const { url } = await posta.urlAllegato(allegato.id, nonControllato)
      if (finestra) { finestra.opener = null; finestra.location.href = url } else window.open(url, '_blank', 'noopener')
    } catch (e) { finestra?.close(); setErrore(testoErrore(e)) }
  }

  // In quarantena (546): il nome e la scritta, senza comando. Il file non sta piu' con gli altri e non si
  // scarica da nessun percorso; lo gestiscono Direzione e Referente privacy da «Quarantena».
  if (allegato.quarantena) {
    return (
      <div className="posta-allegato-riga">
        <span className="posta-allegato posta-allegato-quarantena" title={posta.scrittaQuarantena(allegato.quarantena)}>
          <IconaAvviso />
          <span className="posta-allegato-nome">{allegato.nome}</span>
          <span className="tenue">{posta.peso(allegato.dimensione)}</span>
        </span>
        <span className="piccolo testo-critico">{posta.scrittaQuarantena(allegato.quarantena)}</span>
      </div>
    )
  }

  return (
    <div className="posta-allegato-riga">
      {/* Nome e peso come testo, poi il comando «Scarica» (4/11/2026): prima il comando era il nome stesso, e non
          si capiva che si scaricava da li'. Non ancora controllato (526): si scarica dopo l'avviso; infetto: mai,
          il nome e la scritta senza comando. */}
      <span className={`posta-allegato${infetto ? ' posta-allegato-quarantena' : ''}`}
            title={infetto ? 'Bloccato dall’antivirus' : allegato.nome}>
        {pulito ? <IconaDocumento /> : <IconaAvviso />}
        <span className="posta-allegato-nome">{allegato.nome}</span>
        <span className="tenue">{posta.peso(allegato.dimensione)}</span>
      </span>
      {!infetto && (
        <Bottone variante="secondario" dimensione="piccolo" icona={<IconaScarica />}
                 onClick={() => (pulito ? void scarica() : setAvviso(true))}
                 aria-label={`Scarica ${allegato.nome}`}
                 title={pulito ? `Scarica ${allegato.nome}` : 'Non ancora controllato dall’antivirus: si scarica dopo un avviso'}>
          Scarica
        </Bottone>
      )}
      {!pulito && (
        <span className={`piccolo ${infetto ? 'testo-critico' : 'tenue'}`}>
          {infetto ? 'bloccato dall’antivirus' : 'non ancora controllato dall’antivirus'}
        </span>
      )}
      {avviso && (
        <Modale titolo="Allegato non ancora controllato" chiudi={() => setAvviso(false)} fuocoIniziale="azioni"
          azioni={
            <>
              <Bottone variante="primario" onClick={() => setAvviso(false)}>Annulla</Bottone>
              <Bottone variante="secondario" onClick={() => { setAvviso(false); void scarica(true) }}>
                Scarica comunque
              </Bottone>
            </>
          }>
          <p>
            <strong>{allegato.nome}</strong> non è ancora stato controllato dall’antivirus. Aprilo solo se ti
            aspettavi questo file da questo mittente; nel dubbio chiedi conferma a chi te l’ha mandato con un
            altro mezzo, per esempio al telefono.
          </p>
          <p className="piccolo tenue">Lo scaricamento resta nel registro degli eventi.</p>
        </Modale>
      )}
      {/* Partito come collegamento pubblico (509): stato e scaricamenti, con il registro e la revoca. */}
      {k && (
        <Etichetta tono={k.revocato_il ? 'neutro' : 'accento'}>
          {k.revocato_il ? 'collegamento revocato'
            : `collegamento · ${k.scaricamenti === 1 ? '1 scaricamento' : `${k.scaricamenti} scaricamenti`}`}
        </Etichetta>
      )}
      {k && gestisce && (
        <Bottone variante="discreto" dimensione="piccolo" onClick={() => setRegistro(true)}>Scaricamenti</Bottone>
      )}
      {errore && <span className="piccolo testo-critico">{errore}</span>}
      {registro && k && (
        <RegistroCollegamento postaId={postaId} collegamentoId={k.id} chiudi={() => setRegistro(false)} />
      )}
    </div>
  )
}

/** Che cosa dice una voce del registro di un messaggio a scadenza (510). */
const VOCI_SCADENZA: Record<posta.EventoScadenza['tipo'], string> = {
  apertura: 'pagina aperta', password_errata: 'password sbagliata', blocco: 'pagina bloccata per troppi tentativi',
  scaricamento: 'allegato scaricato', revoca: 'revocato', proroga: 'scadenza cambiata',
}

/**
 * Messaggio spedito a scadenza (510): lo stato per il destinatario e, per chi l'ha spedito o scrive dalla
 * casella, il registro, «Cambia la scadenza» e «Revoca ora».
 */
function BloccoScadenza({ postaId, scadenza, gestisce }: {
  postaId: string; scadenza: NonNullable<posta.Messaggio['scadenza']>; gestisce: boolean
}) {
  const [stato, setStato] = useState(scadenza)
  const [registro, setRegistro] = useState<posta.ScadenzaLetta | null>(null)
  const [aperto, setAperto] = useState(false)
  const [nuova, setNuova] = useState(() => posta.perCampoDataOra(new Date(scadenza.scade_il)))
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  const carica = useCallback(() => {
    posta.leggiScadenza(postaId).then((r) => { setRegistro(r); setStato(r) }).catch((e) => setErrore(testoErrore(e)))
  }, [postaId])

  async function esegui(azione: () => Promise<unknown>) {
    setInCorso(true); setErrore(null)
    try { await azione(); carica() } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(false) }
  }

  return (
    <div className="posta-blocco-scadenza">
      <Etichetta tono={stato.revocata_il || new Date(stato.scade_il) <= new Date() ? 'neutro' : 'attenzione'}>
        {stato.modo === 'messaggio' ? 'Messaggio' : 'Allegati'} {posta.statoScadenza(stato)}
      </Etichetta>
      {gestisce && (
        <Bottone variante="discreto" dimensione="piccolo" onClick={() => { setAperto(true); carica() }}>Scadenza e registro</Bottone>
      )}
      {aperto && (
        <Modale titolo="Messaggio a scadenza" chiudi={() => setAperto(false)}
          azioni={<>
            {!stato.revocata_il && (
              <Bottone variante="critico" inCorso={inCorso} onClick={() => void esegui(() => posta.revocaScadenza(postaId))}>
                Revoca ora
              </Bottone>
            )}
            <Bottone variante="secondario" onClick={() => setAperto(false)}>Chiudi</Bottone>
          </>}>
          {errore && <Avviso tipo="errore">{errore}</Avviso>}
          <p className="piccolo">
            {stato.modo === 'messaggio'
              ? 'Ai destinatari fuori da CIDIS è arrivato un avviso con il collegamento alla pagina del messaggio.'
              : 'Gli allegati sono partiti come collegamento.'} Nel gestionale il messaggio resta com’è.
            {registro?.password ? ' La pagina chiede la password.' : ''}
          </p>
          {!stato.revocata_il && (
            <div className="riga">
              <Campo etichetta="Scade il" tipo="datetime-local" valore={nuova} cambia={setNuova} />
              <Bottone variante="secondario" inCorso={inCorso}
                       onClick={() => void esegui(() => posta.prorogaScadenza(postaId, new Date(nuova).toISOString()))}>
                Cambia la scadenza
              </Bottone>
            </div>
          )}
          {!registro && !errore && <Attesa testo="Caricamento…" />}
          {registro && (
            <Tabella intestazioni={['Quando', 'Che cosa', 'Rete o persona']} vuoto={<Vuoto testo="Ancora nessuno ha aperto la pagina." />}>
              {registro.eventi.map((e, i) => (
                <tr key={i}>
                  <td data-etichetta="Quando" className="piccolo">{quando(e.avvenuto_il)}</td>
                  <td data-etichetta="Che cosa">{VOCI_SCADENZA[e.tipo]}</td>
                  <td data-etichetta="Rete o persona" className="piccolo">
                    {e.chi ?? <span className="mono">{e.ip_troncato ?? '—'}</span>}
                  </td>
                </tr>
              ))}
            </Tabella>
          )}
        </Modale>
      )}
    </div>
  )
}

/** Data e ora brevi, per i registri dei collegamenti. */
const quando = (iso: string) => new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })

/**
 * Il registro di un collegamento (509): quando e da quale rete l'hanno scaricato, e «Revoca il
 * collegamento», che vale subito. Lo vedono chi ha spedito e chi scrive dalla casella.
 */
function RegistroCollegamento({ postaId, collegamentoId, chiudi }: {
  postaId: string; collegamentoId: string; chiudi: () => void
}) {
  const [voce, setVoce] = useState<posta.CollegamentoLetto | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  const carica = useCallback(() => {
    posta.collegamentiDelMessaggio(postaId)
      .then((elenco) => setVoce(elenco.find((k) => k.id === collegamentoId) ?? null))
      .catch((e) => setErrore(testoErrore(e)))
  }, [postaId, collegamentoId])
  useEffect(carica, [carica])

  async function revoca() {
    setInCorso(true); setErrore(null)
    try { await posta.revocaCollegamento(collegamentoId); carica() } catch (e) { setErrore(testoErrore(e)) }
    finally { setInCorso(false) }
  }

  return (
    <Modale titolo={voce ? `Collegamento a «${voce.nome}»` : 'Collegamento'} chiudi={chiudi}
      azioni={<>
        {voce && !voce.revocato_il && (
          <Bottone variante="critico" inCorso={inCorso} onClick={() => void revoca()}>Revoca il collegamento</Bottone>
        )}
        <Bottone variante="secondario" onClick={chiudi}>Chiudi</Bottone>
      </>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!voce && !errore && <Attesa testo="Caricamento…" />}
      {voce && (
        <>
          <p className="piccolo">
            {voce.revocato_il
              ? `Revocato il ${quando(voce.revocato_il)}${voce.motivo_revoca === 'invio_non_riuscito' ? ': il messaggio non è partito' : ''}. Chi apre il collegamento legge «File non disponibile».`
              : voce.scade_il
                ? `Chi ha il collegamento scarica il file fino al ${quando(voce.scade_il)}.`
                : 'Chi ha il collegamento scarica il file finché il messaggio resta nell’archivio, o finché non lo revochi.'}
          </p>
          <Tabella intestazioni={['Scaricato il', 'Rete']} vuoto={<Vuoto testo="Nessuno l’ha ancora scaricato." />}>
            {voce.scaricamenti.map((d, i) => (
              <tr key={i}>
                <td data-etichetta="Scaricato il">{quando(d.scaricato_il)}</td>
                <td data-etichetta="Rete" className="mono piccolo">{d.ip_troncato ?? '—'}</td>
              </tr>
            ))}
          </Tabella>
          <p className="piccolo tenue">Dell’indirizzo di rete si tiene solo la prima parte.</p>
        </>
      )}
    </Modale>
  )
}

/**
 * Tutti i collegamenti agli allegati, con gli scaricamenti (509): per la Referente privacy (o il DPO).
 * La lettura resta nel registro eventi.
 */
function RegistroCollegamenti() {
  const [voci, setVoci] = useState<posta.VoceRegistroCollegamenti[] | null>(null)
  const [aperto, setAperto] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  function apri() {
    setAperto(true)
    posta.registroCollegamenti().then(setVoci).catch((e) => setErrore(testoErrore(e)))
  }

  return (
    <div>
      <div className="posta-titoletto">Collegamenti agli allegati</div>
      {!aperto ? (
        <Bottone variante="discreto" dimensione="piccolo" onClick={apri}>Vedi i collegamenti e gli scaricamenti</Bottone>
      ) : (
        <Modale titolo="Collegamenti agli allegati" larga chiudi={() => setAperto(false)}>
          <p className="piccolo tenue">
            Gli allegati partiti come collegamento pubblico, dal più recente, con chi li ha spediti e ogni
            scaricamento (data e prima parte dell’indirizzo di rete). Questa lettura resta nel registro eventi.
          </p>
          {errore && <Avviso tipo="errore">{errore}</Avviso>}
          {!voci && !errore && <Attesa testo="Caricamento…" />}
          {voci && (
            <Tabella intestazioni={['File', 'Da', 'Spedito', 'Stato', 'Scaricamenti']}
                     vuoto={<Vuoto testo="Nessun collegamento." />}>
              {voci.map((v) => (
                <tr key={v.id}>
                  <td data-etichetta="File">{v.nome} <span className="tenue piccolo">{posta.peso(v.dimensione)}</span></td>
                  <td data-etichetta="Da" className="piccolo">{v.mittente ?? '—'}<div className="tenue">{v.casella}</div></td>
                  <td data-etichetta="Spedito" className="piccolo">{quando(v.creato_il)}</td>
                  <td data-etichetta="Stato" className="piccolo">
                    {v.revocato_il ? `revocato il ${quando(v.revocato_il)}`
                      : v.scade_il ? `fino al ${quando(v.scade_il)}` : 'attivo'}
                  </td>
                  <td data-etichetta="Scaricamenti" className="piccolo">
                    {v.scaricamenti.length === 0 ? '—' : v.scaricamenti.map((d, i) => (
                      <div key={i}>{quando(d.scaricato_il)} <span className="mono tenue">{d.ip_troncato ?? ''}</span></div>
                    ))}
                  </td>
                </tr>
              ))}
            </Tabella>
          )}
        </Modale>
      )}
    </div>
  )
}

// --- collegamento a una scheda ------------------------------------------------------------------

function Collega({ threadId, chiudi, collegato }: {
  threadId: string; chiudi: () => void; collegato: (personaId: string) => void
}) {
  const [testo, setTesto] = useState('')
  const [risultati, setRisultati] = useState<VocePersona[]>([])
  const [cercando, setCercando] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const campoCerca = useRef<HTMLInputElement>(null)

  useEffect(() => { campoCerca.current?.focus() }, [])

  useEffect(() => {
    if (testo.trim().length < 2) { setRisultati([]); return }
    const controllore = new AbortController()
    const attesa = setTimeout(() => {
      setCercando(true)
      api.get<{ risultati: VocePersona[] }>(
        `/persone?cerca=${encodeURIComponent(testo.trim())}`, { segnale: controllore.signal })
        .then((r) => { setRisultati(r.risultati); setErrore(null) })
        .catch((e) => { if ((e as Error).name !== 'AbortError') setErrore(testoErrore(e)) })
        .finally(() => { if (!controllore.signal.aborted) setCercando(false) })
    }, 400)
    return () => { clearTimeout(attesa); controllore.abort() }
  }, [testo])

  async function collega(persona: VocePersona) {
    try {
      await posta.agganciaThread(threadId, { persona_id: persona.id })
      collegato(persona.id)
    } catch (e) { setErrore(testoErrore(e)) }
  }

  return (
    <Modale titolo="Collega la conversazione a una scheda" chiudi={chiudi}>
      <Avviso tipo="informazione">
        Collegata alla scheda, la corrispondenza diventa visibile a chi può vedere quella scheda, e
        il collegamento resta registrato. La ricerca guarda solo le schede che rientrano nei tuoi incarichi.
      </Avviso>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <Campo etichetta="Cerca la scheda" tipo="search" valore={testo} cambia={setTesto}
             segnaposto="nome, cognome o numero di scheda" riferimento={campoCerca} />
      {cercando && <Attesa testo="Ricerca in corso…" />}
      {risultati.map((p) => (
        <button key={p.id} type="button" className="posta-scelta" onClick={() => void collega(p)}>
          <span className="mono">{p.numero_scheda}</span>
          <span>{p.etichetta}</span>
          {p.ha_area_riservata && <span className="conteggio"><IconaLucchetto /></span>}
        </button>
      ))}
      {!cercando && testo.trim().length >= 2 && risultati.length === 0 && (
        <p className="piccolo tenue">Nessuna scheda trovata fra quelle che puoi vedere.</p>
      )}
    </Modale>
  )
}

// --- configurazione delle caselle -----------------------------------------------------------------

function Modulo({ quale, email, nome }: { quale: 'organizzazione' | 'mia'; email: string; nome: string }) {
  const [casella, setCasella] = useState<Casella | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [nuovaSmtp, setNuovaSmtp] = useState('')
  const [nuovaImap, setNuovaImap] = useState('')
  const [predefiniti, setPredefiniti] = useState<Predefiniti | null>(null)
  const [lettura, setLettura] = useState<string | null>(null)

  const carica = useCallback(() => {
    setCasella(null); setMessaggio(null); setErrore(null); setLettura(null)
    // Le due letture vanno insieme: la casella non ancora creata si compone dai predefiniti, e
    // mostrarla prima significherebbe far comparire i campi vuoti e poi riempirli sotto gli
    // occhi di chi sta scrivendo.
    Promise.all([
      api.get<Casella | null>(`/posta/${quale}`),
      api.get<Predefiniti>('/posta/predefinita').catch(() => null),
    ])
      .then(([c, p]) => {
        setPredefiniti(p)
        setCasella(c ?? vuota(
          quale === 'mia' ? email : '',
          quale === 'mia' ? nome : 'C.I.D.I.S. Impresa Sociale ETS',
          quale === 'mia' ? p : null))
      })
      .catch((e) => setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.'))
  }, [quale, email, nome])
  useEffect(carica, [carica])

  if (errore && !casella) return <Avviso tipo="errore">{errore}</Avviso>
  if (!casella) return <Attesa testo="Caricamento…" />

  // Casella ospitata (502): niente server ne' password da configurare.
  if (casella.modalita === 'ospitata') {
    return (
      <>
        <ModuloOspitata quale={quale} iniziale={casella} email={email} salvata={carica} />
        {quale === 'organizzazione' && (
          <>
            <RiquadroPredefiniti iniziali={predefiniti} salvati={() => carica()} />
            <RiquadroAvvisoInCalce />
          </>
        )}
      </>
    )
  }

  const cambia = (campo: keyof Casella) => (valore: string) =>
    setCasella((c) => (c ? { ...c, [campo]: valore } : c))

  async function salva() {
    setInCorso(true); setErrore(null); setMessaggio(null)
    try {
      await api.put(`/posta/${quale}`, {
        nome_visualizzato: casella!.nome_visualizzato,
        indirizzo: casella!.indirizzo,
        smtp_host: casella!.smtp_host,
        smtp_porta: Number(casella!.smtp_porta),
        smtp_sicurezza: casella!.smtp_sicurezza,
        smtp_utente: casella!.smtp_utente,
        smtp_password: nuovaSmtp || undefined,
        imap_host: casella!.imap_host || null,
        imap_porta: casella!.imap_porta ? Number(casella!.imap_porta) : null,
        imap_sicurezza: casella!.imap_sicurezza || null,
        imap_utente: casella!.imap_utente || null,
        imap_password: nuovaImap || null,
        imap_cartelle: casella!.imap_cartelle,
        attiva: casella!.attiva,
      })
      setNuovaSmtp(''); setNuovaImap('')
      setMessaggio('Configurazione salvata.')
      carica()
    } catch (e) { setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.') }
    finally { setInCorso(false) }
  }

  /**
   * «Aggiorna ora»: chiede una lettura supplementare della casella.
   *
   * La lettura non avviene dentro questa richiesta — l'API accoda un lavoro e fa partire il Job
   * (apps/api/src/rotte/posta.ts) — quindi qui non si aspetta l'esito: si dice che la richiesta
   * e' partita e la data dell'ultima lettura si aggiorna alla riapertura della pagina.
   */
  async function aggiornaOra() {
    setInCorso(true); setErrore(null); setMessaggio(null); setLettura(null)
    try {
      await api.post(`/posta/caselle/${casella!.id}/sincronizza`)
      setLettura('Lettura richiesta: i messaggi nuovi compaiono entro qualche minuto.')
    } catch (e) { setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.') }
    finally { setInCorso(false) }
  }

  async function prova() {
    setInCorso(true); setErrore(null); setMessaggio(null)
    try {
      const esito = await api.post<{ credenziali: boolean; messaggio_inviato: boolean; errore: string | null }>(
        `/posta/${quale}/prova`)
      if (esito.messaggio_inviato) setMessaggio(`Messaggio di prova inviato a ${email}.`)
      else if (esito.credenziali) setErrore(`Credenziali accettate, invio non riuscito: ${esito.errore}`)
      else setErrore(`Credenziali rifiutate dal server di posta: ${esito.errore}`)
    } catch (e) { setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.') }
    finally { setInCorso(false) }
  }

  return (
    <>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}
      <AvvisoCifratura smtp={casella.smtp_sicurezza} imap={casella.imap_host ? casella.imap_sicurezza : null} />
      {casella.ultimo_errore && !errore && (
        <Avviso tipo="attenzione">Ultimo tentativo non riuscito: {casella.ultimo_errore}</Avviso>
      )}

      <Riquadro
        titolo="Invio (SMTP)"
        azioni={
          <>
            <Etichetta tono={casella.attiva ? 'positivo' : 'neutro'}>
              {casella.attiva ? 'attiva' : 'non attiva'}
            </Etichetta>
            {casella.ultima_prova && (
              <span className="piccolo tenue">
                provata il {new Date(casella.ultima_prova).toLocaleString('it-IT')}
              </span>
            )}
          </>
        }
      >
        <div className="griglia">
          <Campo etichetta="Nome che appare al destinatario" obbligatorio
                 valore={casella.nome_visualizzato} cambia={cambia('nome_visualizzato')} />
          <Campo etichetta="Indirizzo" tipo="email" obbligatorio
                 valore={casella.indirizzo} cambia={cambia('indirizzo')}
                 disabilitato={quale === 'mia'}
                 aiuto={quale === 'mia' ? 'Coincide con l’indirizzo di accesso al sistema.' : undefined} />
          <Campo etichetta="Server in uscita" obbligatorio segnaposto="smtp.cidisonlus.org"
                 valore={casella.smtp_host} cambia={cambia('smtp_host')} />
          <Campo etichetta="Porta" tipo="number"
                 valore={casella.smtp_porta} cambia={cambia('smtp_porta')} />
          <Scelta etichetta="Sicurezza" opzioni={SICUREZZA_SMTP}
                  {...sceltaSicurezza(casella.smtp_sicurezza, 'starttls')} cambia={cambia('smtp_sicurezza')} />
          <Campo etichetta="Utente" obbligatorio
                 valore={casella.smtp_utente} cambia={cambia('smtp_utente')} />
          <Campo etichetta="Password" tipo="password" valore={nuovaSmtp} cambia={setNuovaSmtp}
                 segnaposto={casella.smtp_password ? '•••••••• (già impostata)' : ''}
                 autoComplete="new-password"
                 aiuto="Si scrive ma non si rilegge: resta cifrata in banca dati." />
        </div>
      </Riquadro>

      <Riquadro
        titolo="Lettura (IMAP)"
        azioni={
          <>
            <Etichetta>facoltativa</Etichetta>
            <span className="piccolo tenue">
              {casella.imap_ultima_sincro
                ? `ultima lettura ${new Date(casella.imap_ultima_sincro).toLocaleString('it-IT')}`
                : 'mai letta'}
            </span>
            {/* Il comando esiste solo su una casella gia' salvata e con un server dichiarato:
                senza quelli non c'e' niente da leggere. */}
            {casella.id && casella.imap_host && casella.attiva && (
              <Bottone variante="secondario" disabled={inCorso} onClick={() => void aggiornaOra()}>
                Aggiorna ora
              </Bottone>
            )}
          </>
        }
      >
        {lettura && <Avviso tipo="successo">{lettura}</Avviso>}
        {quale === 'mia' && !casella.id && predefiniti?.imap_host && (
          <Avviso tipo="informazione">
            Server, porte e cartelle sono già quelli dell’ente: ti basta scrivere la password.
          </Avviso>
        )}
        <Avviso tipo="informazione">
          Con IMAP attivo, le email scambiate con un beneficiario in anagrafica confluiscono nella
          sua scheda. Il resto della casella viene ignorato e non viene conservato. L’importazione
          parte solo quando la funzione «Importazione della posta» è attiva.
        </Avviso>
        <div className="griglia">
          <Campo etichetta="Server in entrata" segnaposto="imap.cidisonlus.org"
                 valore={casella.imap_host ?? ''} cambia={cambia('imap_host')} />
          <Campo etichetta="Porta" tipo="number"
                 valore={casella.imap_porta ?? 993} cambia={cambia('imap_porta')} />
          <Scelta etichetta="Sicurezza" opzioni={SICUREZZA_IMAP}
                  {...sceltaSicurezza(casella.imap_sicurezza, 'tls')} cambia={cambia('imap_sicurezza')} />
          <Campo etichetta="Utente" valore={casella.imap_utente ?? ''}
                 cambia={cambia('imap_utente')} />
          <Campo etichetta="Password" tipo="password" valore={nuovaImap} cambia={setNuovaImap}
                 segnaposto={casella.imap_password
                   ? '•••••••• (già impostata)'
                   : 'come quella di invio, se uguale'}
                 autoComplete="new-password" />
          <Campo etichetta="Cartelle da leggere" valore={casella.imap_cartelle.join(', ')}
                 cambia={(v) => setCasella((c) => (c
                   ? { ...c, imap_cartelle: v.split(',').map((s) => s.trim()).filter(Boolean) }
                   : c))}
                 aiuto="Separate da virgola. Di norma basta INBOX." />
        </div>
      </Riquadro>

      <Riquadro>
        <Spunta etichetta="Usa questa casella" valore={casella.attiva}
                cambia={(v) => setCasella((c) => (c ? { ...c, attiva: v } : c))} />
        <div className="riga">
          <Bottone variante="primario" disabled={inCorso} onClick={() => void salva()}>
            Salva
          </Bottone>
          <Bottone variante="secondario" disabled={inCorso || !casella.id}
                   onClick={() => void prova()}>
            Prova l’invio
          </Bottone>
        </div>
      </Riquadro>

      {quale === 'organizzazione' && (
        <>
          <RiquadroPredefiniti iniziali={predefiniti} salvati={() => carica()} />
          <RiquadroAvvisoInCalce />
        </>
      )}
    </>
  )
}

/**
 * Casella ospitata, personale o dell'ente (502). Il titolare cambia il nome che vede chi riceve; della
 * casella dell'ente si cambiano anche indirizzo e stato. «Prova l'invio» manda un messaggio di prova
 * dal servizio di invio (TEM per la casella dell'ente, il server di posta di CIDIS per le altre)
 * all'indirizzo di chi la preme.
 */
function ModuloOspitata({ quale, iniziale, email, salvata }: {
  quale: 'organizzazione' | 'mia'; iniziale: Casella; email: string; salvata: () => void
}) {
  const [nome, setNome] = useState(iniziale.nome_visualizzato)
  const [indirizzo, setIndirizzo] = useState(iniziale.indirizzo)
  const [attiva, setAttiva] = useState(iniziale.attiva)
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)
  const ente = quale === 'organizzazione'

  async function salva() {
    setInCorso(true); setErrore(null); setMessaggio(null)
    try {
      await api.put(`/posta/${quale}`, ente
        ? { nome_visualizzato: nome, indirizzo: indirizzo.trim(), attiva }
        : { nome_visualizzato: nome })
      setMessaggio('Configurazione salvata.')
      salvata()
    } catch (e) { setErrore(testoErrore(e)) }
    finally { setInCorso(false) }
  }

  async function prova() {
    setInCorso(true); setErrore(null); setMessaggio(null)
    try {
      const esito = await api.post<{ credenziali: boolean; messaggio_inviato: boolean; errore: string | null }>(
        `/posta/${quale}/prova`)
      if (esito.messaggio_inviato) setMessaggio(`Messaggio di prova inviato a ${email}.`)
      else setErrore(`Invio non riuscito: ${esito.errore}`)
    } catch (e) { setErrore(testoErrore(e)) }
    finally { setInCorso(false) }
  }

  return (
    <>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}
      <Riquadro titolo={ente ? 'Casella dell’ente' : 'La tua casella'}
        azioni={
          <>
            <Etichetta tono="accento">ospitata</Etichetta>
            <Etichetta tono={iniziale.attiva ? 'positivo' : 'neutro'}>
              {iniziale.attiva ? 'attiva' : 'non attiva'}
            </Etichetta>
          </>
        }>
        <Avviso tipo="informazione">
          La posta di questa casella arriva direttamente nel gestionale e parte dal servizio di invio
          di CIDIS: non ci sono server né password da configurare. Nella posta si entra con l’accesso
          al gestionale.
        </Avviso>
        <div className="griglia">
          <Campo etichetta="Nome che appare al destinatario" obbligatorio valore={nome} cambia={setNome} />
          <Campo etichetta="Indirizzo" tipo="email" obbligatorio valore={indirizzo} cambia={setIndirizzo}
                 disabilitato={!ente}
                 aiuto={ente ? undefined : 'Coincide con l’indirizzo di accesso al gestionale.'} />
        </div>
        <p className="piccolo tenue">
          Ricezione: {posta.ultimoMessaggio(iniziale.ultimo_arrivo_il ?? null)}.
        </p>
        {ente && <Spunta etichetta="Usa questa casella" valore={attiva} cambia={setAttiva} />}
        <div className="riga">
          <Bottone variante="primario" disabled={inCorso || nome.trim().length < 2} onClick={() => void salva()}>
            Salva
          </Bottone>
          <Bottone variante="secondario" disabled={inCorso} onClick={() => void prova()}>
            Prova l’invio
          </Bottone>
        </div>
      </Riquadro>
    </>
  )
}

/**
 * Valori predefiniti delle caselle personali (CHT-28).
 *
 * Sta sotto la casella dell'ente perche' e' la stessa persona a decidere i due, e perche' i
 * numeri sono gli stessi: chi ha appena configurato l'invio dell'organizzazione ha sotto gli
 * occhi quello che deve ricopiare qui. Non ci sono credenziali: la password resta di chi la
 * scrive, e la casella personale non e' leggibile da nessun altro.
 */
function RiquadroPredefiniti({ iniziali, salvati }: {
  iniziali: Predefiniti | null
  salvati: () => void
}) {
  const [valori, setValori] = useState<Predefiniti | null>(iniziali)
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)

  // `iniziali` arriva dopo la lettura dell'API: finche' non c'e', qui non si mostra un modulo
  // vuoto che poi cambia sotto le dita.
  useEffect(() => { setValori(iniziali) }, [iniziali])
  if (!valori) return null

  const cambia = (campo: keyof Predefiniti) => (v: string) =>
    setValori((p) => (p ? { ...p, [campo]: v } : p))

  async function salva() {
    setInCorso(true); setErrore(null); setMessaggio(null)
    try {
      await api.put('/posta/predefinita', {
        smtp_host: valori!.smtp_host,
        smtp_porta: Number(valori!.smtp_porta),
        smtp_sicurezza: valori!.smtp_sicurezza,
        imap_host: valori!.imap_host,
        imap_porta: Number(valori!.imap_porta),
        imap_sicurezza: valori!.imap_sicurezza,
        imap_cartelle: valori!.imap_cartelle,
      })
      setMessaggio('Valori predefiniti salvati.')
      salvati()
    } catch (e) { setErrore(testoErrore(e)) }
    finally { setInCorso(false) }
  }

  return (
    <Riquadro titolo="Valori predefiniti per le caselle personali">
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}
      <Avviso tipo="informazione">
        Quando un operatore apre la propria casella personale trova questi valori già scritti e
        compila la sola password. Qui non si conservano credenziali.
      </Avviso>
      <div className="griglia">
        <Campo etichetta="Server in uscita" segnaposto="smtp.cidisonlus.org"
               valore={valori.smtp_host} cambia={cambia('smtp_host')} />
        <Campo etichetta="Porta in uscita" tipo="number"
               valore={valori.smtp_porta} cambia={cambia('smtp_porta')} />
        <Scelta etichetta="Sicurezza in uscita" opzioni={SICUREZZA_SMTP}
                valore={valori.smtp_sicurezza} cambia={cambia('smtp_sicurezza')} />
        <Campo etichetta="Server in entrata" segnaposto="imap.cidisonlus.org"
               valore={valori.imap_host} cambia={cambia('imap_host')} />
        <Campo etichetta="Porta in entrata" tipo="number"
               valore={valori.imap_porta} cambia={cambia('imap_porta')} />
        <Scelta etichetta="Sicurezza in entrata" opzioni={SICUREZZA_IMAP}
                valore={valori.imap_sicurezza} cambia={cambia('imap_sicurezza')} />
        <Campo etichetta="Cartelle da leggere" valore={valori.imap_cartelle.join(', ')}
               cambia={(v) => setValori((p) => (p
                 ? { ...p, imap_cartelle: v.split(',').map((x) => x.trim()).filter(Boolean) }
                 : p))}
               aiuto="Separate da virgola. Vuoto significa la sola INBOX." />
      </div>
      <div className="riga">
        <Bottone variante="primario" disabled={inCorso} onClick={() => void salva()}>
          Salva i valori predefiniti
        </Bottone>
      </div>
    </Riquadro>
  )
}

// --- caselle e ricezione (502) --------------------------------------------------------------------

const AMBITI: Record<posta.AmbitoCasella, string> = {
  organizzazione: 'ente', utente: 'personale', gruppo: 'di gruppo', progetto: 'di progetto',
}

/**
 * Tutte le caselle con la modalita' (IMAP o ospitata), lo stato della ricezione, la conversione e la
 * creazione delle personali ospitate.
 *
 * La linguetta c'e' per chi gestisce le utenze (personali e di gruppo) o la casella dell'ente: ognuno
 * converte le sue, e l'API lo verifica. La conversione si conferma in una finestra che dice che cosa
 * cambia: e' il momento in cui la lettura dal server si ferma.
 */
function CaselleERicezione() {
  const { puo } = useSessione()
  const [dati, setDati] = useState<{ caselle: posta.CasellaAmministrata[]; ricezione: posta.StatoRicezione } | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)
  const [daConvertire, setDaConvertire] = useState<posta.CasellaAmministrata | null>(null)
  const [daArchiviare, setDaArchiviare] = useState<posta.CasellaAmministrata | null>(null)
  const [nuove, setNuove] = useState(false)
  // Inoltro in deroga (536): la casella a cui concederlo, e il contatore che fa rileggere l'elenco.
  const [daInoltrare, setDaInoltrare] = useState<posta.CasellaAmministrata | null>(null)
  const [versioneInoltri, setVersioneInoltri] = useState(0)

  const carica = useCallback(() => {
    posta.elencaCaselleAmministrate().then(setDati).catch((e) => setErrore(testoErrore(e)))
  }, [])
  useEffect(carica, [carica])

  const puoConvertire = (c: posta.CasellaAmministrata) => c.modalita === 'imap'
    && (c.ambito === 'organizzazione' ? puo('impostazione.modifica') : puo('utente.gestisci'))

  if (errore && !dati) return <Avviso tipo="errore">{errore}</Avviso>
  if (!dati) return <Attesa testo="Caricamento…" />
  const { ricezione } = dati
  const ospitate = dati.caselle.filter((c) => c.modalita === 'ospitata').length

  return (
    <>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}

      <Riquadro titolo="Ricezione">
        {ricezione.in_attesa_dal && (
          <Avviso tipo="attenzione">
            La posta in arrivo è ferma dal {new Date(ricezione.in_attesa_dal).toLocaleString('it-IT')}: la
            funzione «Webmail e archiviazione integrale della posta» non è attiva. I messaggi restano nel
            deposito della ricezione e si salvano all’accensione.
          </Avviso>
        )}
        <p>
          Ricezione: {posta.ultimoMessaggio(ricezione.ultimo_arrivo_il)}. Ultimo contatto della
          ricezione: {posta.quandoBreve(ricezione.ultima_chiamata_il)}.
        </p>
        <p className="piccolo tenue">
          {ospitate} caselle ospitate su {dati.caselle.length}. Un messaggio per una casella ospitata arriva
          nel gestionale entro un minuto di giorno, entro un quarto d’ora di notte.
        </p>
      </Riquadro>

      <Riquadro titolo="Caselle" denso
        azioni={puo('utente.gestisci') && (
          <Bottone variante="primario" dimensione="piccolo" icona={<IconaPiu />}
                   onClick={() => { setMessaggio(null); setNuove(true) }}>
            Caselle personali ospitate
          </Bottone>
        )}>
        <Avviso tipo="informazione">
          «Ospitata»: la casella sta nel gestionale, senza server né password; la posta arriva dalla
          ricezione di CIDIS e parte dal servizio di invio. «IMAP»: la casella sta su un server di posta
          che il gestionale legge. Una casella IMAP si converte quando la posta del suo indirizzo passa
          alla ricezione del gestionale: conversazioni, cartelle e stato restano.
        </Avviso>
        <Tabella intestazioni={['Casella', 'Tipo', 'Modalità', 'Ricezione', 'Stato',
                                <span className="solo-lettore" key="azioni">Azioni</span>]}
                 vuoto={<Vuoto testo="Nessuna casella configurata." />}>
          {dati.caselle.map((c) => (
            <tr key={c.id}>
              <td data-etichetta="Casella">
                <div className="voce-principale">{c.nome_visualizzato}</div>
                <div className="piccolo tenue">{c.indirizzo}</div>
              </td>
              <td data-etichetta="Tipo" className="piccolo">
                {AMBITI[c.ambito]}
                {c.titolare && <div className="tenue">{c.titolare.cognome} {c.titolare.nome}</div>}
                {c.progetto && <div className="tenue">{c.progetto.codice_cidis ?? c.progetto.titolo}</div>}
                {c.ambito === 'gruppo' && <div className="tenue">{c.membri === 1 ? '1 persona' : `${c.membri} persone`}</div>}
                {c.da_associare && <Etichetta tono="attenzione">da associare</Etichetta>}
              </td>
              <td data-etichetta="Modalità">
                {c.modalita === 'ospitata'
                  ? <Etichetta tono="accento">ospitata</Etichetta>
                  : <Etichetta>IMAP</Etichetta>}
              </td>
              <td data-etichetta="Ricezione" className="piccolo">
                {c.modalita === 'ospitata'
                  ? posta.ultimoMessaggio(c.ultimo_arrivo_il)
                  : !c.lettura_imap ? 'senza lettura'
                    : c.imap_ultima_sincro ? `ultima lettura ${posta.quandoBreve(c.imap_ultima_sincro)}` : 'mai letta'}
                <div className="tenue">{c.messaggi === 1 ? '1 messaggio' : `${c.messaggi} messaggi`}</div>
              </td>
              <td data-etichetta="Stato">
                <Etichetta tono={c.attiva ? 'positivo' : 'neutro'}>
                  {c.archiviata ? 'archiviata' : c.attiva ? 'attiva' : 'disattivata'}
                </Etichetta>
                {c.inoltro && (
                  <div className="piccolo">
                    <Etichetta tono="attenzione">inoltro in deroga</Etichetta>
                    <div className="tenue">verso {c.inoltro.destinazione}</div>
                  </div>
                )}
                {c.ultimo_errore && c.modalita === 'imap' && (
                  <div className="piccolo testo-critico">{c.ultimo_errore}</div>
                )}
              </td>
              <td data-etichetta="Azioni">
                {puoConvertire(c) && (
                  <Bottone dimensione="piccolo" aria-label={`Converti ${c.indirizzo} in casella ospitata`}
                           onClick={() => { setMessaggio(null); setDaConvertire(c) }}>
                    Converti in ospitata
                  </Bottone>
                )}
                {/* «Archivia» (507): personali, di gruppo e di progetto, non la casella dell'ente. */}
                {puo('utente.gestisci') && c.ambito !== 'organizzazione' && !c.archiviata && (
                  <Bottone dimensione="piccolo" aria-label={`Archivia ${c.indirizzo}`}
                           onClick={() => { setMessaggio(null); setDaArchiviare(c) }}>
                    Archivia
                  </Bottone>
                )}
                {/* Inoltro in deroga (536): solo per le ospitate attive; la revoca e' nell'elenco sotto. */}
                {puo('utente.gestisci') && c.modalita === 'ospitata' && c.attiva && !c.archiviata && !c.inoltro && (
                  <Bottone dimensione="piccolo" aria-label={`Inoltro in deroga di ${c.indirizzo}`}
                           onClick={() => { setMessaggio(null); setDaInoltrare(c) }}>
                    Inoltro in deroga
                  </Bottone>
                )}
              </td>
            </tr>
          ))}
        </Tabella>
      </Riquadro>

      {daConvertire && (
        <ConfermaConversione casella={daConvertire} chiudi={() => setDaConvertire(null)}
          convertita={() => {
            setMessaggio(`${daConvertire.indirizzo} è ora una casella ospitata.`)
            setDaConvertire(null)
            carica()
          }} />
      )}
      {daArchiviare && (
        <ConfermaArchiviazione casella={daArchiviare} chiudi={() => setDaArchiviare(null)}
          archiviata={(testo) => { setDaArchiviare(null); setMessaggio(testo); carica() }} />
      )}
      {nuove && (
        <NuovePersonaliOspitate chiudi={() => setNuove(false)}
          create={(testo) => { setNuove(false); setMessaggio(testo); carica() }} />
      )}

      {puo('utente.gestisci') && (
        <Riquadro>
          <InoltriInDeroga aggiornato={versioneInoltri} revocato={carica} />
        </Riquadro>
      )}
      {/* Programmi di posta (547): una credenziale per persona sulla stessa casella, con «Revoca». */}
      {puo('utente.gestisci') && (
        <Riquadro>
          <CredenzialiProgramma />
        </Riquadro>
      )}
      {daInoltrare && (
        <ConcediInoltro casella={daInoltrare} chiudi={() => setDaInoltrare(null)}
          concesso={(testo) => {
            setDaInoltrare(null); setMessaggio(testo); carica(); setVersioneInoltri((v) => v + 1)
          }} />
      )}
    </>
  )
}

function ConfermaConversione({ casella, chiudi, convertita }: {
  casella: posta.CasellaAmministrata; chiudi: () => void; convertita: () => void
}) {
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  async function converti() {
    setInCorso(true); setErrore(null)
    try {
      await posta.convertiInOspitata(casella.id)
      convertita()
    } catch (e) { setErrore(testoErrore(e)) }
    finally { setInCorso(false) }
  }

  return (
    <Modale titolo={`Convertire ${casella.indirizzo} in casella ospitata`} chiudi={chiudi} fuocoIniziale="azioni"
      azioni={
        <>
          <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
          <Bottone variante="primario" inCorso={inCorso} onClick={() => void converti()}>Converti</Bottone>
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <p>Che cosa succede:</p>
      <ul>
        <li>conversazioni, cartelle, letto e non letto, archivio e cestino restano come sono;</li>
        <li>la lettura dal server di posta si ferma, e le password del server si cancellano;</li>
        <li>da qui in avanti la posta arriva dalla ricezione del gestionale e parte dal servizio di invio.</li>
      </ul>
      <p className="piccolo tenue">
        Va fatto quando la posta di questo indirizzo passa alla ricezione del gestionale. I messaggi
        rimasti sul vecchio server si caricano dal suo backup: lo fa chi gestisce il sistema.
      </p>
    </Modale>
  )
}

/**
 * Caselle personali ospitate per le persone scelte: l'indirizzo e' quello con cui entrano nel
 * gestionale. Il filtro sul dominio propone le sole persone con l'indirizzo di CIDIS.
 */
function NuovePersonaliOspitate({ chiudi, create }: { chiudi: () => void; create: (testo: string) => void }) {
  const [utenti, setUtenti] = useState<posta.UtenteSenzaCasella[] | null>(null)
  const [dominio, setDominio] = useState('cidisonlus.org')
  const [scelti, setScelti] = useState<Set<string>>(new Set())
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  useEffect(() => {
    posta.utentiSenzaCasella().then(setUtenti).catch((e) => setErrore(testoErrore(e)))
  }, [])

  const filtro = dominio.trim().toLowerCase().replace(/^@/, '')
  const visibili = (utenti ?? []).filter((u) => !filtro || u.email.toLowerCase().endsWith(`@${filtro}`))
  const tutti = visibili.length > 0 && visibili.every((u) => scelti.has(u.id))
  const quanti = visibili.filter((u) => scelti.has(u.id)).length

  function alterna(id: string, si: boolean) {
    setScelti((prima) => {
      const dopo = new Set(prima)
      if (si) dopo.add(id)
      else dopo.delete(id)
      return dopo
    })
  }

  async function crea() {
    setInCorso(true); setErrore(null)
    try {
      const ids = visibili.filter((u) => scelti.has(u.id)).map((u) => u.id)
      const esito = await posta.creaPersonaliOspitate(ids)
      create(`Caselle ospitate create: ${esito.create}.`
        + (esito.saltate ? ` Saltate perché la persona ha già una casella o non è in uso: ${esito.saltate}.` : '')
        + (esito.indirizzi_in_uso ? ` Indirizzi già usati da un’altra casella ospitata: ${esito.indirizzi_in_uso}.` : ''))
    } catch (e) { setErrore(testoErrore(e)) }
    finally { setInCorso(false) }
  }

  return (
    <Modale titolo="Caselle personali ospitate" larga chiudi={chiudi}
      azioni={
        <>
          <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
          <Bottone variante="primario" inCorso={inCorso} disabled={!quanti} onClick={() => void crea()}>
            {quanti === 0 ? 'Crea le caselle' : quanti === 1 ? 'Crea 1 casella' : `Crea ${quanti} caselle`}
          </Bottone>
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <p className="piccolo tenue">
        Ognuna ha l’indirizzo con cui la persona entra nel gestionale, il suo nome e cognome come nome
        visualizzato, ed è attiva subito. Qui ci sono le persone in uso che non hanno ancora una casella.
      </p>
      <Campo etichetta="Dominio" valore={dominio} cambia={setDominio} aiuto="Vuoto: tutte le persone." />
      {!utenti ? <Attesa testo="Caricamento…" /> : visibili.length === 0
        ? <Vuoto testo="Nessuna persona senza casella con questo dominio." />
        : (
          <>
            <Spunta etichetta={`Tutte (${visibili.length})`} valore={tutti}
                    cambia={(si) => setScelti(si ? new Set(visibili.map((u) => u.id)) : new Set())} />
            {visibili.map((u) => (
              <Spunta key={u.id} etichetta={`${u.cognome} ${u.nome} · ${u.email}`} valore={scelti.has(u.id)}
                      cambia={(si) => alterna(u.id, si)} />
            ))}
          </>
        )}
    </Modale>
  )
}

// --- caselle di gruppo (483) ----------------------------------------------------------------------

/**
 * Caselle condivise: le caselle di gruppo, con chi le usa, creazione, modifica, disattivazione; quelle
 * da associare (504), con i tre modi di associarle; le caselle di progetto.
 *
 * La linguetta c'e' per chi ha `utente.gestisci` (la Direzione, le risorse umane). Le persone si
 * associano anche dalla scheda dell'utente (Amministrazione → Utenti → Modifica, «Caselle di gruppo»),
 * che e' il posto in cui si decide che cosa una persona puo' fare. `soloDaAssociare` arriva dalla riga
 * in cima alla pagina («Associale»): l'elenco parte filtrato.
 */
function CaselleDiGruppo({ soloDaAssociare = false }: { soloDaAssociare?: boolean }) {
  const { aggiorna } = useSessione()
  const [elenco, setElenco] = useState<posta.CasellaGruppo[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)
  const [aperta, setAperta] = useState<posta.CasellaGruppo | 'nuova' | null>(null)
  const [daAssociare, setDaAssociare] = useState<posta.CasellaGruppo | null>(null)
  const [daArchiviare, setDaArchiviare] = useState<posta.CasellaGruppo | null>(null)
  const [filtro, setFiltro] = useState(soloDaAssociare)
  const [predefiniti, setPredefiniti] = useState<Predefiniti | null>(null)
  const [versione, setVersione] = useState(0)

  const carica = useCallback(() => {
    posta.elencaGruppi().then(setElenco).catch((e) => setErrore(testoErrore(e)))
  }, [])
  useEffect(carica, [carica])
  useEffect(() => { setFiltro(soloDaAssociare) }, [soloDaAssociare])
  useEffect(() => {
    api.get<Predefiniti>('/posta/predefinita').then(setPredefiniti).catch(() => undefined)
  }, [])

  const quanteDaAssociare = (elenco ?? []).filter((c) => c.da_associare).length
  const visibili = (elenco ?? []).filter((c) => !filtro || c.da_associare)

  /** Dopo un'associazione: elenco, caselle di progetto e conteggio della riga in cima alla pagina. */
  function associata(testo: string) {
    setDaAssociare(null)
    setMessaggio(testo)
    carica()
    setVersione((v) => v + 1)
    void aggiorna().catch(() => undefined)
  }

  return (
    <>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}
      <Riquadro titolo="Caselle di gruppo" denso
        azioni={
          <Bottone variante="primario" dimensione="piccolo" icona={<IconaPiu />}
                   onClick={() => { setMessaggio(null); setAperta('nuova') }}>
            Nuova casella di gruppo
          </Bottone>
        }>
        <Avviso tipo="informazione">
          Una casella di gruppo è usata da più persone, che la trovano in Posta accanto alla propria,
          con il livello «legge» oppure «legge e scrive». Ospitata, sta nel gestionale senza server né
          password; IMAP, ha indirizzo e password su un server di posta. «Da associare»: la casella non
          ha ancora nessuno. Con «Associa» diventa la casella di un progetto, di alcune persone o la
          casella personale di qualcuno.
        </Avviso>
        {quanteDaAssociare > 0 && (
          <Spunta etichetta={`Solo le caselle da associare (${quanteDaAssociare})`} valore={filtro} cambia={setFiltro} />
        )}
        {!elenco ? <Attesa testo="Caricamento…" /> : (
          <Tabella intestazioni={['Casella', 'Chi la usa', 'Stato', 'Ricezione',
                                  <span className="solo-lettore" key="azioni">Azioni</span>]}
                   vuoto={<Vuoto testo={filtro ? 'Nessuna casella da associare.' : 'Nessuna casella di gruppo.'} />}>
            {visibili.map((c) => (
              <tr key={c.id}>
                <td data-etichetta="Casella">
                  <div className="voce-principale">{c.nome_visualizzato}</div>
                  <div className="piccolo tenue">{c.indirizzo}</div>
                  {c.da_associare && <Etichetta tono="attenzione">da associare</Etichetta>}
                </td>
                <td data-etichetta="Chi la usa">
                  {c.membri.length === 0 ? <span className="tenue piccolo">Nessuno</span> : (
                    <div className="riga-etichette">
                      {c.membri.map((m) => (
                        <Etichetta key={m.id} tono={m.stato === 'attivo' ? 'neutro' : 'attenzione'}>
                          {m.cognome} {m.nome}{m.livello === 'legge' ? ' · legge' : ''}
                          {m.stato === 'attivo' ? '' : ` · ${m.stato}`}
                        </Etichetta>
                      ))}
                    </div>
                  )}
                </td>
                <td data-etichetta="Stato">
                  <Etichetta tono={c.attiva ? 'positivo' : 'neutro'}>
                    {c.archiviata ? 'archiviata' : c.attiva ? 'attiva' : 'disattivata'}
                  </Etichetta>
                  {c.modalita === 'ospitata' && <Etichetta tono="accento">ospitata</Etichetta>}
                  {(c.smtp_sicurezza === 'nessuna' || c.imap_sicurezza === 'nessuna') && (
                    <div className="piccolo testo-critico">sicurezza «nessuna»: va corretta</div>
                  )}
                  {c.ultimo_errore && <div className="piccolo testo-critico">{c.ultimo_errore}</div>}
                </td>
                <td data-etichetta="Ricezione" className="piccolo">
                  {c.modalita === 'ospitata'
                    ? posta.ultimoMessaggio(c.ultimo_arrivo_il)
                    : c.imap_ultima_sincro
                      ? `ultima lettura ${new Date(c.imap_ultima_sincro).toLocaleString('it-IT')}`
                      : c.imap_host ? 'mai letta' : 'senza lettura'}
                </td>
                <td data-etichetta="Azioni">
                  <span className="riga riga-a-destra">
                    <Bottone dimensione="piccolo" variante={c.da_associare ? 'primario' : 'secondario'}
                             aria-label={`Associa ${c.indirizzo}`}
                             onClick={() => { setMessaggio(null); setDaAssociare(c) }}>
                      Associa
                    </Bottone>
                    <Bottone dimensione="piccolo" aria-label={`Modifica ${c.indirizzo}`}
                             onClick={() => { setMessaggio(null); setAperta(c) }}>
                      Modifica
                    </Bottone>
                    {/* Richiesta di Carmem (1/11/2026): in un comando solo la disattivazione con il rifiuto e
                        l'archivio delle caselle (507), come «Archivia» in «Caselle e ricezione». */}
                    {!c.archiviata && (
                      <Bottone dimensione="piccolo" aria-label={`Disattiva e archivia ${c.indirizzo}`}
                               onClick={() => { setMessaggio(null); setDaArchiviare(c) }}>
                        Disattiva e archivia
                      </Bottone>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </Tabella>
        )}
      </Riquadro>

      <CaselleDiProgetto versione={versione} />

      {aperta && (
        <ModuloGruppo iniziale={aperta === 'nuova' ? null : aperta} predefiniti={predefiniti}
                      chiudi={() => setAperta(null)} firmaSalvata={carica}
                      salvata={(testo) => { setAperta(null); setMessaggio(testo); carica() }} />
      )}
      {daAssociare && (
        <AssociaCasella casella={daAssociare} chiudi={() => setDaAssociare(null)} associata={associata} />
      )}
      {daArchiviare && (
        <ConfermaArchiviazione casella={daArchiviare} motivoIniziale="indirizzo" azione="Disattiva e archivia"
          chiudi={() => setDaArchiviare(null)}
          archiviata={(testo) => { setDaArchiviare(null); setMessaggio(testo); carica(); void aggiorna().catch(() => undefined) }} />
      )}
    </>
  )
}

/**
 * Creazione e modifica di una casella di gruppo. Gli stessi campi della casella dell'ente; per una
 * casella nuova server e porte sono gia' quelli predefiniti, e utente vuoto vale l'indirizzo della
 * casella. La password si scrive ma non si rilegge; lasciata vuota in modifica resta quella di prima.
 */
function ModuloGruppo({ iniziale, predefiniti, chiudi, salvata, firmaSalvata }: {
  iniziale: posta.CasellaGruppo | null
  predefiniti: Predefiniti | null
  chiudi: () => void
  salvata: (messaggio: string) => void
  /** La firma della casella (508) si salva a parte: l'elenco si rilegge senza chiudere. */
  firmaSalvata: () => void
}) {
  const [casella, setCasella] = useState<Casella>(() => iniziale ?? vuota('', '', predefiniti))
  // Una casella nuova nasce ospitata (decisione del 31/10/2026); IMAP resta per quelle sul server.
  const [modalita, setModalita] = useState<posta.Modalita>(iniziale?.modalita ?? 'ospitata')
  const ospitata = modalita === 'ospitata'
  const [nuovaSmtp, setNuovaSmtp] = useState('')
  const [nuovaImap, setNuovaImap] = useState('')
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [esitoProva, setEsitoProva] = useState<string | null>(null)

  const cambia = (campo: keyof Casella) => (valore: string) =>
    setCasella((c) => ({ ...c, [campo]: valore }))

  async function salva() {
    setInCorso(true); setErrore(null)
    const corpo = ospitata ? {
      modalita: 'ospitata',
      nome_visualizzato: casella.nome_visualizzato,
      indirizzo: casella.indirizzo.trim(),
      attiva: casella.attiva,
    } : {
      nome_visualizzato: casella.nome_visualizzato,
      indirizzo: casella.indirizzo.trim(),
      smtp_host: casella.smtp_host,
      smtp_porta: Number(casella.smtp_porta),
      smtp_sicurezza: casella.smtp_sicurezza,
      // Vuoto: l'API usa l'indirizzo della casella, che sul server dell'ente e' l'utente di accesso.
      smtp_utente: casella.smtp_utente || undefined,
      smtp_password: nuovaSmtp || undefined,
      imap_host: casella.imap_host || null,
      imap_porta: casella.imap_porta ? Number(casella.imap_porta) : null,
      imap_sicurezza: casella.imap_sicurezza || null,
      imap_utente: casella.imap_utente || undefined,
      imap_password: nuovaImap || null,
      imap_cartelle: casella.imap_cartelle,
      attiva: casella.attiva,
    }
    try {
      if (iniziale) await api.put(`/posta/gruppi/${iniziale.id}`, corpo)
      else await api.post('/posta/gruppi', corpo)
      salvata(iniziale
        ? `Casella ${corpo.indirizzo} aggiornata.`
        : `Casella ${corpo.indirizzo} creata: ora si assegna alle persone dalla loro scheda utente.`)
    } catch (e) { setErrore(testoErrore(e)) }
    finally { setInCorso(false) }
  }

  async function prova() {
    if (!iniziale) return
    setInCorso(true); setErrore(null); setEsitoProva(null)
    try {
      const esito = await api.post<{ credenziali: boolean; messaggio_inviato: boolean; errore: string | null }>(
        `/posta/gruppi/${iniziale.id}/prova`)
      if (esito.messaggio_inviato) setEsitoProva('Messaggio di prova inviato al tuo indirizzo.')
      else if (esito.credenziali) setErrore(`Credenziali accettate, invio non riuscito: ${esito.errore}`)
      else setErrore(`Credenziali rifiutate dal server di posta: ${esito.errore}`)
    } catch (e) { setErrore(testoErrore(e)) }
    finally { setInCorso(false) }
  }

  const pronta = casella.nome_visualizzato.trim().length >= 2 && casella.indirizzo.includes('@')
    && (!!iniziale || ospitata || nuovaSmtp.length > 0)

  return (
    <Modale titolo={iniziale ? `Casella di gruppo ${iniziale.indirizzo}` : 'Nuova casella di gruppo'}
      larga chiudi={chiudi}
      azioni={
        <>
          <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
          {iniziale && (
            <Bottone variante="secondario" disabled={inCorso} onClick={() => void prova()}>
              Prova l’invio
            </Bottone>
          )}
          <Bottone variante="primario" inCorso={inCorso} disabled={!pronta} onClick={() => void salva()}>
            Salva
          </Bottone>
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {esitoProva && <Avviso tipo="successo">{esitoProva}</Avviso>}
      {!ospitata && (
        <AvvisoCifratura smtp={casella.smtp_sicurezza} imap={casella.imap_host ? casella.imap_sicurezza : null} />
      )}

      {/* La modalita' si sceglie alla creazione; una casella IMAP si converte da «Caselle e ricezione». */}
      {!iniziale && (
        <Scelta etichetta="Modalità" valore={modalita} cambia={(v) => setModalita(v as posta.Modalita)}
                opzioni={[
                  { valore: 'ospitata', etichetta: 'Ospitata nel gestionale (nessun server, nessuna password)' },
                  { valore: 'imap', etichetta: 'Su un server di posta (IMAP e SMTP)' },
                ]} />
      )}
      <div className="griglia">
        <Campo etichetta="Nome che appare al destinatario" obbligatorio segnaposto="Qualità CIDIS"
               valore={casella.nome_visualizzato} cambia={cambia('nome_visualizzato')} />
        <Campo etichetta="Indirizzo" tipo="email" obbligatorio segnaposto="qualita@cidisonlus.org"
               valore={casella.indirizzo} cambia={cambia('indirizzo')} />
      </div>
      {ospitata && iniziale && (
        <p className="piccolo tenue">
          Ricezione: {posta.ultimoMessaggio(iniziale.ultimo_arrivo_il)}.
        </p>
      )}

      {!ospitata && (<>
      <h3 className="operatori-titoletto">Invio (SMTP)</h3>
      <div className="griglia">
        <Campo etichetta="Server in uscita" obbligatorio segnaposto="smtp.cidisonlus.org"
               valore={casella.smtp_host} cambia={cambia('smtp_host')} />
        <Campo etichetta="Porta" tipo="number"
               valore={casella.smtp_porta} cambia={cambia('smtp_porta')} />
        <Scelta etichetta="Sicurezza" opzioni={SICUREZZA_SMTP}
                {...sceltaSicurezza(casella.smtp_sicurezza, 'starttls')} cambia={cambia('smtp_sicurezza')} />
        <Campo etichetta="Utente" valore={casella.smtp_utente} cambia={cambia('smtp_utente')}
               aiuto="Vuoto: l’indirizzo della casella." />
        <Campo etichetta="Password" tipo="password" valore={nuovaSmtp} cambia={setNuovaSmtp}
               obbligatorio={!iniziale}
               segnaposto={casella.smtp_password ? '•••••••• (già impostata)' : ''}
               autoComplete="new-password"
               aiuto="Si scrive ma non si rilegge: resta cifrata. Vuota, resta quella di prima." />
      </div>

      <h3 className="operatori-titoletto">Lettura (IMAP)</h3>
      <div className="griglia">
        <Campo etichetta="Server in entrata" segnaposto="imap.cidisonlus.org"
               valore={casella.imap_host ?? ''} cambia={cambia('imap_host')}
               aiuto="Vuoto: la casella si usa solo per scrivere." />
        <Campo etichetta="Porta" tipo="number"
               valore={casella.imap_porta ?? 993} cambia={cambia('imap_porta')} />
        <Scelta etichetta="Sicurezza" opzioni={SICUREZZA_IMAP}
                {...sceltaSicurezza(casella.imap_sicurezza, 'tls')} cambia={cambia('imap_sicurezza')} />
        <Campo etichetta="Utente" valore={casella.imap_utente ?? ''} cambia={cambia('imap_utente')}
               aiuto="Vuoto: l’indirizzo della casella." />
        <Campo etichetta="Password" tipo="password" valore={nuovaImap} cambia={setNuovaImap}
               segnaposto={casella.imap_password ? '•••••••• (già impostata)' : 'come quella di invio, se uguale'}
               autoComplete="new-password" />
        <Campo etichetta="Cartelle da leggere" valore={casella.imap_cartelle.join(', ')}
               cambia={(v) => setCasella((c) => ({
                 ...c, imap_cartelle: v.split(',').map((x) => x.trim()).filter(Boolean),
               }))}
               aiuto="Separate da virgola. Di norma basta INBOX." />
      </div>
      </>)}

      <Spunta etichetta="Casella attiva" valore={casella.attiva}
              cambia={(v) => setCasella((c) => ({ ...c, attiva: v }))} />
      {/* Firma propria della casella (508): si salva a parte, con il suo comando. */}
      {iniziale && <FirmaDellaCasella casellaId={iniziale.id} firma={iniziale.firma ?? null} salvata={firmaSalvata} />}
      <p className="piccolo tenue">
        {ospitata
          ? 'Disattivata, la casella sparisce subito dalla posta di chi la usa e la ricezione rifiuta i '
            + 'messaggi nuovi per questo indirizzo. I messaggi già arrivati restano e tornano con la riattivazione.'
          : 'Disattivata, la casella sparisce subito dalla posta di chi la usa e non si legge più dal '
            + 'server. I messaggi già arrivati restano e tornano con la riattivazione.'}
      </p>
      {/* Chi la usa da un programma di posta (547): c'e' solo se qualcuno ha la credenziale. */}
      {iniziale && ospitata && <CredenzialiProgramma casellaId={iniziale.id} />}
    </Modale>
  )
}
